import { Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { validateAndFocusFirstError } from '../../utils/form-validation';
import { friendlyError } from '../../utils/friendly-error';
import { BusinessConfigService } from '../../services/business-config.service';
import { AuthService } from '../../services/auth.service';
import { PrivacyPolicyService } from '../../services/privacy-policy.service';
import { UpdateBusinessProfileRequest, ChangePasswordRequest, ChangeEmailResponse, PrivacyPolicy, CreatePrivacyPolicyRequest, UpdatePrivacyPolicyRequest } from '../../models/sala.model';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { ActionCooldownService } from '../../services/action-cooldown.service';
import { ResponsiveTableDirective } from '../../directives/responsive-table.directive';
import { AdminTabsComponent } from '../ui/admin-tabs.component';
import { CancellationPolicySettingsComponent } from './cancellation-policy-settings.component';
import { CooldownBubbleDirective } from '../../directives/cooldown-bubble.directive';
import { PhotoTipsComponent } from '../ui/photo-tips.component';
import { RichTextEditorComponent } from '../ui/rich-text-editor.component';

type ConfigTab = 'negocio' | 'politicas';

@Component({
  selector: 'app-configuracion',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ResponsiveTableDirective, AdminTabsComponent, CooldownBubbleDirective, CancellationPolicySettingsComponent, PhotoTipsComponent, RichTextEditorComponent],
  template: `
    <div class="configuracion-container">
      <div class="configuracion-content">
        <section class="config-hero" aria-label="Panel de configuración">
          <div>
            <span class="hero-kicker">CENTRO DE CONFIGURACIÓN</span>
            <h2>Controla la identidad y seguridad de tu negocio</h2>
            <p>Actualiza perfil comercial, accesos críticos y políticas visibles para clientes desde un solo lugar.</p>
          </div>
          <div class="hero-badges">
            <span><i class="bi bi-shield-check"></i> Seguridad</span>
            <span><i class="bi bi-building"></i> Marca</span>
            <span><i class="bi bi-file-earmark-lock2"></i> Privacidad</span>
          </div>
        </section>
        <app-admin-tabs
          [tabs]="configTabs"
          [active]="activeTab()"
          ariaLabel="Secciones de configuración"
          (tabSelect)="selectTab($event)"
        ></app-admin-tabs>

        <!-- Información e identidad: datos del negocio a la izquierda; logo y
             seguridad (acciones puntuales) en una columna lateral compacta. -->
        <div *ngIf="activeTab() === 'negocio'" class="identity-layout">
        <!-- Sección de Información del Perfil -->
        <section
          id="panel-informacion-negocio"
          class="config-section tab-panel identity-card business-info-card"
          role="tabpanel"
          aria-labelledby="tab-informacion-negocio"
        >
          <header class="identity-card-head">
            <span class="identity-card-icon" aria-hidden="true"><i class="bi bi-building-gear"></i></span>
            <div class="identity-card-title">
              <h3>Información del negocio</h3>
              <p>Así identifican tus clientes a tu negocio en tu sitio, reservas y correos.</p>
            </div>
            <button *ngIf="!showEditProfile()" type="button" class="btn-edit identity-action" (click)="abrirEdicionPerfil()">
              <i class="bi bi-pencil-square" aria-hidden="true"></i><span>Editar</span>
            </button>
          </header>

          <div id="panel-identidad-visual" class="identity-profile">
            <div class="logo-section">
              <div class="logo-preview" [class.has-logo]="showLogoImage()">
                <img
                  *ngIf="showLogoImage()"
                  [src]="logoPreview() || getLogoUrl(currentBusiness()?.logo)"
                  alt="Logo del negocio"
                  class="logo-image"
                  (error)="logoLoadFailed.set(true)"
                />
                <div *ngIf="!showLogoImage()" class="no-logo">
                  <span class="no-logo-initials">{{ businessInitials() }}</span>
                  <small>{{ currentBusiness()?.logo && logoLoadFailed() ? 'Logo no disponible' : 'Sin logo' }}</small>
                </div>
              </div>
            </div>
            <div class="identity-profile-main">
              <span class="identity-kicker">Nombre comercial</span>
              <strong class="identity-name">{{ currentBusiness()?.name || 'Tu negocio' }}</strong>
              <span class="identity-owner-chip">
                <span class="identity-owner-avatar" aria-hidden="true">{{ ownerInitials() }}</span>
                Propietario: <b>{{ currentBusiness()?.owner?.name || (isLoading() ? 'Cargando…' : 'Sin asignar') }}</b>
              </span>
            </div>
            <div class="logo-upload">
              <input
                type="file"
                id="logoInput"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                (change)="onLogoSelected($event)"
                class="file-input"
              />
              <strong class="logo-file-name" *ngIf="selectedLogo() as file">{{ file.name }}</strong>
              <div class="logo-actions">
                <label *ngIf="!selectedLogo()" for="logoInput" class="file-label">
                  <i class="bi bi-cloud-arrow-up" aria-hidden="true"></i>
                  {{ currentBusiness()?.logo ? 'Cambiar logo' : 'Seleccionar logo' }}
                </label>
                <button
                  *ngIf="selectedLogo()"
                  type="button"
                  (click)="subirLogo()"
                  class="btn-upload"
                  [disabled]="isUploadingLogo()"
                  cooldownBubble data-cooldown-key="config-upload-logo"
                >
                  <i class="bi bi-check2-circle" aria-hidden="true"></i>
                  {{ isUploadingLogo() ? 'Subiendo...' : 'Guardar logo' }}
                </button>
                <button
                  *ngIf="selectedLogo()"
                  type="button"
                  (click)="cancelarLogo()"
                  class="btn-cancel-small"
                  [disabled]="isUploadingLogo()"
                >
                  Cancelar
                </button>
              </div>
              <small class="hint">PNG, JPG, WEBP o GIF · ideal cuadrado de 512 px o más</small>
              <app-photo-tips kind="logo"></app-photo-tips>
            </div>
          </div>

          <div class="identity-facts">
            <div class="identity-fact">
              <span class="identity-fact-label"><i class="bi bi-briefcase" aria-hidden="true"></i> Razón social</span>
              <span class="identity-fact-value">{{ currentBusiness()?.company || 'No especificada' }}</span>
            </div>
            <div class="identity-fact">
              <span class="identity-fact-label"><i class="bi bi-telephone" aria-hidden="true"></i> Teléfono</span>
              <span class="identity-fact-value">{{ currentBusiness()?.phone || 'No especificado' }}</span>
            </div>
            <div class="identity-fact">
              <span class="identity-fact-label"><i class="bi bi-globe2" aria-hidden="true"></i> Sitio web</span>
              <span class="identity-fact-value">
                <a *ngIf="currentBusiness()?.website as web; else noWeb" [href]="web" target="_blank" rel="noopener noreferrer">{{ web }}</a>
                <ng-template #noWeb><span class="identity-empty">No especificado</span></ng-template>
              </span>
            </div>
            <div class="identity-fact">
              <span class="identity-fact-label"><i class="bi bi-envelope" aria-hidden="true"></i> Correo de contacto</span>
              <span class="identity-fact-value">{{ currentBusiness()?.email || 'No especificado' }}</span>
              <button type="button" class="link-to-mi-sitio identity-fact-link" (click)="irAMiSitio.emit()">Se cambia en Mi sitio &rarr;</button>
            </div>
          </div>

          <div *ngIf="showEditProfile()" class="settings-modal-overlay" (click)="cancelarEdicionPerfil()">
          <form #profileFormRef [formGroup]="profileForm" (ngSubmit)="guardarPerfil()" class="settings-modal-content profile-form profile-edit-layout" role="dialog" aria-modal="true" aria-labelledby="profile-modal-title" (click)="$event.stopPropagation()">
            <div class="settings-modal-header profile-modal-header">
              <span class="profile-modal-icon" aria-hidden="true"><i class="bi bi-building-gear"></i></span>
              <div class="profile-modal-heading">
                <span class="modal-kicker">INFORMACIÓN DEL NEGOCIO</span>
                <h3 id="profile-modal-title">Editar perfil comercial</h3>
                <p>Estos datos identifican a tu negocio dentro de TuSala.</p>
              </div>
              <button type="button" class="policy-modal-close" (click)="cancelarEdicionPerfil()" aria-label="Cerrar edición del perfil"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
            </div>
            <div class="settings-modal-body profile-modal-body">
            <fieldset class="profile-group">
              <legend><i class="bi bi-shop" aria-hidden="true"></i> Datos comerciales</legend>
              <div class="profile-form-grid">
              <div class="form-group field-name">
                <label for="name">Nombre comercial *</label>
                <input
                  type="text"
                  id="name"
                  formControlName="name"
                  class="form-control"
                  placeholder="Ej: Hotel Plaza"
                  autocomplete="organization"
                  minlength="2"
                  maxlength="100"
                />
                <small *ngIf="profileForm.get('name')?.hasError('required') && fieldInteracted('name')" class="error">
                  El nombre es requerido
                </small>
                <small *ngIf="profileForm.get('name')?.hasError('minlength') && fieldInteracted('name')" class="error">Ingresa al menos 2 caracteres.</small>
                <small *ngIf="profileForm.get('name')?.hasError('maxlength') && fieldInteracted('name')" class="error">El nombre no puede superar 100 caracteres.</small>
                <small *ngIf="profileForm.get('name')?.hasError('pattern') && fieldInteracted('name')" class="error">No se permiten caracteres de control.</small>
              </div>

              <div class="form-group field-company">
                <label for="company">Razón social *</label>
                <input
                  type="text"
                  id="company"
                  formControlName="company"
                  class="form-control"
                  placeholder="Ej: Plaza Hotels S.A."
                  autocomplete="organization"
                  minlength="2"
                  maxlength="100"
                />
                <small *ngIf="profileForm.get('company')?.hasError('required') && fieldInteracted('company')" class="error">
                  La razón social es requerida
                </small>
                <small *ngIf="profileForm.get('company')?.hasError('minlength') && fieldInteracted('company')" class="error">Ingresa al menos 2 caracteres.</small>
                <small *ngIf="profileForm.get('company')?.hasError('maxlength') && fieldInteracted('company')" class="error">La razón social no puede superar 100 caracteres.</small>
              </div>

              <div class="form-group field-phone">
                <label for="phone">Teléfono *</label>
                <input
                  type="text"
                  id="phone"
                  formControlName="phone"
                  class="form-control"
                  placeholder="+503 2222-3333"
                  inputmode="tel"
                  autocomplete="tel"
                  maxlength="16"
                  (input)="sanitizePhoneInput($event)"
                />
                <small *ngIf="profileForm.get('phone')?.hasError('required') && fieldInteracted('phone')" class="error">
                  El teléfono es requerido
                </small>
                <small *ngIf="profileForm.get('phone')?.hasError('pattern') && fieldInteracted('phone')" class="error">Ingresa entre 7 y 15 dígitos; solamente se admite un signo + al inicio.</small>
              </div>

              <div class="form-group field-website">
                <label for="website">Sitio web <span class="optional-tag">Opcional</span></label>
                <input
                  type="url"
                  id="website"
                  formControlName="website"
                  class="form-control"
                  placeholder="https://www.ejemplo.com"
                  inputmode="url"
                  autocomplete="url"
                  maxlength="255"
                />
                <small *ngIf="profileForm.get('website')?.hasError('pattern') && fieldInteracted('website')" class="error">Usa una dirección válida que comience con http:// o https://</small>
                <small *ngIf="profileForm.get('website')?.hasError('maxlength') && fieldInteracted('website')" class="error">El sitio web no puede superar 255 caracteres.</small>
              </div>
              </div>
            </fieldset>

            <fieldset class="profile-group">
              <legend><i class="bi bi-envelope" aria-hidden="true"></i> Correos</legend>
              <div class="profile-form-grid">
              <div class="form-group field-email">
                <label for="email">Correo de acceso (inicio de sesión) *</label>
                <input
                  type="email"
                  id="email"
                  formControlName="email"
                  class="form-control"
                  placeholder="tu@correo.com"
                  autocomplete="email"
                  maxlength="100"
                  [readonly]="businessContextMode"
                  [class.readonly-field]="businessContextMode"
                />
                <small *ngIf="profileForm.get('email')?.hasError('required') && fieldInteracted('email')" class="error">
                  El correo es requerido
                </small>
                <small *ngIf="profileForm.get('email')?.hasError('pattern') && fieldInteracted('email')" class="error">
                  Ingresa un correo electrónico válido
                </small>
                <small class="hint" *ngIf="!businessContextMode">Si lo cambias, te pediremos tu contraseña actual para confirmarlo.</small>
                <small class="hint" *ngIf="businessContextMode">El correo de acceso del propietario se cambia desde Super Admin › Usuarios.</small>
              </div>

              <div class="form-group locked-email-group">
                <span class="locked-email-label">Correo de contacto (público)</span>
                <div class="locked-email" aria-label="Correo de contacto del negocio">
                  <div class="locked-email-icon"><i class="bi bi-lock-fill" aria-hidden="true"></i></div>
                  <strong class="locked-email-value">{{ currentBusiness()?.email || 'No disponible' }}</strong>
                </div>
                <small class="hint">
                  Se cambia en
                  <button type="button" class="link-to-mi-sitio" (click)="irAMiSitio.emit()">Mi sitio &rarr; Información de contacto</button>.
                </small>
              </div>
              </div>
            </fieldset>
            </div>
            <div class="form-actions">
              <button type="button" class="btn-cancel" (click)="cancelarEdicionPerfil()" [disabled]="isUpdatingProfile()">Cancelar</button>
              <button type="submit" class="btn-save" [disabled]="isUpdatingProfile()" cooldownBubble data-cooldown-key="config-save-profile">
                <i class="bi bi-check2-circle" aria-hidden="true"></i>
                {{ isUpdatingProfile() ? 'Guardando...' : 'Guardar cambios' }}
              </button>
            </div>
          </form>
          </div>
        </section>

        <!-- Sección de Contraseña -->
        <section class="config-section tab-panel identity-card security-card">
          <header class="identity-card-head">
            <span class="identity-card-icon" aria-hidden="true"><i class="bi bi-shield-lock"></i></span>
            <div class="identity-card-title">
              <h3>{{ businessContextMode ? 'Acceso del propietario' : 'Seguridad de la cuenta' }}</h3>
              <p>{{ businessContextMode ? 'Credenciales con las que el propietario inicia sesión.' : 'Credenciales con las que inicias sesión en el panel.' }}</p>
            </div>
          </header>

          <div *ngIf="!businessContextMode" class="security-rows">
            <div class="security-row">
              <span class="security-row-icon" aria-hidden="true"><i class="bi bi-envelope-at"></i></span>
              <div>
                <small>Correo de acceso</small>
                <strong>{{ currentBusiness()?.session_user?.email || 'No disponible' }}</strong>
              </div>
            </div>
            <div class="security-row">
              <span class="security-row-icon" aria-hidden="true"><i class="bi bi-key"></i></span>
              <div>
                <small>Contraseña</small>
                <strong>••••••••</strong>
              </div>
              <button *ngIf="!showChangePassword()" type="button" class="btn-edit identity-action" (click)="abrirCambioPassword()">
                <i class="bi bi-arrow-repeat" aria-hidden="true"></i><span>Cambiar</span>
              </button>
            </div>
          </div>

          <div *ngIf="businessContextMode" class="context-access-card">
            <span class="context-access-icon"><i class="bi bi-person-lock" aria-hidden="true"></i></span>
            <div>
              <strong>{{ currentBusiness()?.session_user?.name || 'Propietario' }}</strong>
              <small>{{ currentBusiness()?.session_user?.email || 'Sin correo registrado' }}</small>
              <p>La contraseña y el correo de acceso del propietario se gestionan desde Super Admin: puedes enviar un enlace de recuperación, asignar una contraseña nueva o cerrar sus sesiones.</p>
            </div>
            <button type="button" class="btn-edit" (click)="manageOwnerAccount()"><i class="bi bi-key" aria-hidden="true"></i> Gestionar acceso</button>
          </div>

          <div *ngIf="showChangePassword() && !businessContextMode" class="settings-modal-overlay" (click)="cancelarCambioPassword()">
          <form #passwordFormRef [formGroup]="passwordForm" (ngSubmit)="cambiarContrasena()" class="settings-modal-content password-form" role="dialog" aria-modal="true" aria-labelledby="password-modal-title" (click)="$event.stopPropagation()">
            <div class="settings-modal-header">
              <div><span class="modal-kicker">SEGURIDAD</span><h3 id="password-modal-title">Cambiar contraseña</h3></div>
              <button type="button" class="policy-modal-close" (click)="cancelarCambioPassword()" aria-label="Cerrar cambio de contraseña"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
            </div>
            <div class="settings-modal-body">
            <div class="form-group">
              <label for="current_password">Contraseña Actual *</label>
              <div class="password-visibility-field">
              <input 
                [type]="showCurrentPassword() ? 'text' : 'password'"
                id="current_password" 
                formControlName="current_password" 
                class="form-control"
                placeholder="Ingresa tu contraseña actual"
                maxlength="200"
              />
              <button type="button" class="password-visibility-btn" (click)="showCurrentPassword.set(!showCurrentPassword())" [attr.aria-label]="showCurrentPassword() ? 'Ocultar contraseña actual' : 'Mostrar contraseña actual'" [attr.aria-pressed]="showCurrentPassword()"><i class="bi" [class.bi-eye]="!showCurrentPassword()" [class.bi-eye-slash]="showCurrentPassword()" aria-hidden="true"></i></button>
              </div>
              <small *ngIf="passwordForm.get('current_password')?.hasError('required') && passwordForm.get('current_password')?.touched" class="error">
                La contraseña actual es requerida
              </small>
            </div>

            <div class="form-group">
              <label for="new_password">Nueva Contraseña *</label>
              <div class="password-visibility-field">
              <input 
                [type]="showNewPassword() ? 'text' : 'password'"
                id="new_password" 
                formControlName="new_password" 
                class="form-control"
                placeholder="6 o más caracteres"
                maxlength="200"
              />
              <button type="button" class="password-visibility-btn" (click)="showNewPassword.set(!showNewPassword())" [attr.aria-label]="showNewPassword() ? 'Ocultar nueva contraseña' : 'Mostrar nueva contraseña'" [attr.aria-pressed]="showNewPassword()"><i class="bi" [class.bi-eye]="!showNewPassword()" [class.bi-eye-slash]="showNewPassword()" aria-hidden="true"></i></button>
              </div>
              <small *ngIf="passwordForm.get('new_password')?.hasError('required') && passwordForm.get('new_password')?.touched" class="error">
                La nueva contraseña es requerida
              </small>
              <small *ngIf="passwordForm.get('new_password')?.hasError('minlength') && passwordForm.get('new_password')?.touched" class="error">
                Debe tener al menos 6 caracteres.
              </small>
              <div class="password-rule-list"><span [class.ok]="passwordRule()">Mínimo 6 caracteres</span></div>
            </div>

            <div class="form-group">
              <label for="confirm_password">Confirmar Nueva Contraseña *</label>
              <div class="password-visibility-field">
              <input 
                [type]="showConfirmPassword() ? 'text' : 'password'"
                id="confirm_password" 
                formControlName="confirm_password" 
                class="form-control"
                placeholder="Confirma tu nueva contraseña"
                maxlength="200"
              />
              <button type="button" class="password-visibility-btn" (click)="showConfirmPassword.set(!showConfirmPassword())" [attr.aria-label]="showConfirmPassword() ? 'Ocultar confirmación de contraseña' : 'Mostrar confirmación de contraseña'" [attr.aria-pressed]="showConfirmPassword()"><i class="bi" [class.bi-eye]="!showConfirmPassword()" [class.bi-eye-slash]="showConfirmPassword()" aria-hidden="true"></i></button>
              </div>
              <small *ngIf="passwordForm.get('confirm_password')?.hasError('required') && passwordForm.get('confirm_password')?.touched" class="error">
                Debes confirmar la contraseña
              </small>
              <small *ngIf="passwordForm.hasError('passwordMismatch') && passwordForm.get('confirm_password')?.touched" class="error">
                Las contraseñas no coinciden
              </small>
              <small *ngIf="passwordForm.get('confirm_password')?.value" class="password-match-state" [class.ok]="passwordsMatch()">{{passwordsMatch() ? 'Las contraseñas coinciden.' : 'Las contraseñas todavía no coinciden.'}}</small>
            </div>

            </div>
            <div class="form-actions">
              <button type="button" class="btn-cancel" (click)="cancelarCambioPassword()" [disabled]="isChangingPassword()">Cancelar</button>
              <button type="submit" class="btn-save" [disabled]="isChangingPassword()" cooldownBubble data-cooldown-key="config-change-password">
                {{ isChangingPassword() ? 'Cambiando...' : 'Cambiar Contraseña' }}
              </button>
            </div>
          </form>
          </div>
        </section>
        </div>

        <!-- Sección de Políticas de Privacidad -->
        <div
          *ngIf="activeTab() === 'politicas'"
          id="panel-politicas-negocio"
          class="config-section tab-panel"
          role="tabpanel"
          aria-labelledby="tab-politicas-negocio"
        >
          <!-- Política de cancelación propia del negocio (Kubo la pide al configurar). -->
          <app-cancellation-policy-settings></app-cancellation-policy-settings>

          <div class="section-header privacy-policy-header">
            <div class="section-title-copy">
              <h3><i class="bi bi-file-earmark-lock2"></i> Políticas de Privacidad</h3>
              <p>Define el aviso que explica a tus clientes qué información recopila el negocio y cómo será utilizada.</p>
            </div>
            <button 
              (click)="abrirNuevaPolitica()"
              class="btn-edit"
              *ngIf="!showCreatePolicyForm() && !editingPolicy()"
            >
              Nueva Política
            </button>
          </div>

          <aside class="policy-guide" *ngIf="!showCreatePolicyForm() && !editingPolicy()">
            <i class="bi bi-shield-check"></i>
            <div><strong>¿Para qué sirve esta sección?</strong><span>Puedes publicar varias políticas al mismo tiempo cuando cubren temas distintos. Las políticas inactivas se conservan como borradores o historial.</span></div>
            <div class="policy-guide-steps"><span><b>1</b> Crea o edita</span><span><b>2</b> Revisa</span><span><b>3</b> Publica las necesarias</span></div>
          </aside>

          <!-- Lista de políticas -->
          <div *ngIf="!showCreatePolicyForm() && !editingPolicy()" class="policies-list">
            <div *ngIf="privacyPolicyService.policies().length === 0 && !isLoadingPolicies()" class="empty-state">
              <p>No hay políticas de privacidad registradas.</p>
            </div>

            <div *ngIf="privacyPolicyService.policies().length > 0" class="policies-table-container">
              <table appResponsiveTable class="policies-table">
                <colgroup>
                  <col class="policy-col-title" />
                  <col class="policy-col-version" />
                  <col class="policy-col-status" />
                  <col class="policy-col-date" />
                  <col class="policy-col-date" />
                  <col class="policy-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Versión</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th>Última Actualización</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let policy of privacyPolicyService.policies()">
                    <td>{{ policy.title }}</td>
                    <td>{{ policy.version || 'N/A' }}</td>
                    <td>
                      <span [class]="'status-' + (policy.is_active ? 'active' : 'inactive')">
                        {{ policy.is_active ? 'Activa' : 'Inactiva' }}
                      </span>
                    </td>
                    <td>{{ formatDate(policy.created_at) }}</td>
                    <td>{{ formatDate(policy.updated_at) }}</td>
                    <td class="actions-cell">
                      <div class="policy-actions" role="group" [attr.aria-label]="'Acciones para ' + policy.title">
                      <button
                        (click)="verPolitica(policy)"
                        class="btn-action btn-view"
                        title="Ver"
                        aria-label="Ver política"
                      >
                        <i class="bi bi-eye"></i>
                      </button>
                      <button
                        (click)="editarPolitica(policy)"
                        class="btn-action btn-edit-small"
                        title="Editar"
                        aria-label="Editar política"
                      >
                        <i class="bi bi-pencil-square"></i>
                      </button>
                      <button
                        *ngIf="!policy.is_active"
                        (click)="activarPolitica(policy.id)"
                        class="btn-action btn-activate"
                        title="Activar"
                        aria-label="Activar política"
                      >
                        <i class="bi bi-check-circle"></i>
                      </button>
                      <button
                        *ngIf="policy.is_active"
                        (click)="desactivarPolitica(policy.id)"
                        class="btn-action btn-deactivate"
                        title="Desactivar"
                        aria-label="Desactivar política"
                      >
                        <i class="bi bi-pause-circle"></i>
                      </button>
                      <button
                        (click)="eliminarPolitica(policy.id)"
                        class="btn-action btn-delete"
                        title="Eliminar"
                        aria-label="Eliminar política"
                      >
                        <i class="bi bi-trash3"></i>
                      </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Formulario de crear/editar política -->
          <div *ngIf="showCreatePolicyForm() || editingPolicy()" class="settings-modal-overlay" (click)="solicitarCerrarFormularioPolitica()">
          <form
            #policyFormRef
            [formGroup]="policyForm"
            (ngSubmit)="guardarPolitica()"
            class="settings-modal-content policy-form pf-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="policy-form-modal-title"
            (click)="$event.stopPropagation()"
            novalidate
          >
            <div class="settings-modal-header pf-header">
              <span class="pf-badge" aria-hidden="true"><i class="bi" [class.bi-file-earmark-plus]="!editingPolicy()" [class.bi-pencil-square]="!!editingPolicy()"></i></span>
              <div class="pf-heading">
                <span class="modal-kicker">POLÍTICAS DEL NEGOCIO</span>
                <h3 id="policy-form-modal-title">{{ editingPolicy() ? 'Editar política' : 'Nueva política' }}</h3>
                <p>Lo que tus clientes leen y aceptan antes de confirmar una reserva.</p>
              </div>
              <button type="button" class="policy-modal-close" (click)="solicitarCerrarFormularioPolitica()" aria-label="Cerrar formulario de política"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
            </div>

            <div class="settings-modal-body pf-body">
              <!-- Paso 1: identificación -->
              <section class="pf-section pf-unified" aria-labelledby="pf-step-1">
                <header class="pf-section-head">
                  <span class="pf-step">1</span>
                  <div><h4 id="pf-step-1">Datos generales</h4><small>Un nombre claro ayuda a distinguir versiones.</small></div>
                </header>

                <div class="pf-general-row">
                  <div class="form-group pf-field pf-title">
                    <label for="title" class="pf-label">Título <em class="pf-req">*</em><small class="pf-count" [class.warn]="policyLength('title') > 230">{{ policyLength('title') }}/255</small></label>
                    <input
                      type="text"
                      id="title"
                      formControlName="title"
                      class="form-control"
                      placeholder="Ej: Política de privacidad y reservas"
                      maxlength="255"
                      autocomplete="off"
                      data-no-limit-hint
                    />
                    <small *ngIf="policyForm.get('title')?.invalid && policyForm.get('title')?.touched" class="error">
                      {{ policyForm.get('title')?.hasError('maxlength') ? 'El título no puede superar 255 caracteres.' : 'Escribe un título para la política.' }}
                    </small>
                  </div>
                  <label class="pf-activate" [class.on]="policyForm.get('is_active')?.value">
                    <input type="checkbox" formControlName="is_active" class="pf-activate-input" />
                    <span class="pf-switch" aria-hidden="true"><span></span></span>
                    <span class="pf-activate-text">
                      <strong>{{ policyForm.get('is_active')?.value ? 'Publicar política' : 'Guardar sin publicar' }}</strong>
                      <small>{{ policyForm.get('is_active')?.value ? 'Visible para los clientes.' : 'Podrás publicarla después.' }}</small>
                    </span>
                    <i class="bi pf-activate-icon" [class.bi-broadcast]="policyForm.get('is_active')?.value" [class.bi-archive]="!policyForm.get('is_active')?.value" aria-hidden="true"></i>
                  </label>
                </div>
                <div class="pf-section-divider" aria-hidden="true"></div>
                <header class="pf-section-head pf-content-head">
                  <span class="pf-step">2</span>
                  <div><h4 id="pf-step-2">Redacta tu política <em class="pf-req">*</em></h4><small>Escribe directamente y utiliza la barra para organizar y destacar la información.</small></div>
                  <button type="button" class="pf-template-btn" [class.active]="showPolicyTemplatePicker()" (click)="showPolicyTemplatePicker.set(!showPolicyTemplatePicker())"><i class="bi bi-stars" aria-hidden="true"></i><span>{{ showPolicyTemplatePicker() ? 'Ocultar opciones' : 'Elegir plantilla' }}</span></button>
                </header>

                <div class="pf-template-picker" *ngIf="showPolicyTemplatePicker()" role="dialog" aria-modal="false" aria-label="Plantillas disponibles">
                  <div class="pf-template-intro">
                    <div><strong>Empieza con una estructura preparada</strong><small>Podrás modificar libremente todo el contenido después.</small></div>
                    <button type="button" (click)="showPolicyTemplatePicker.set(false)" aria-label="Cerrar opciones de plantillas"><i class="bi bi-x-lg"></i></button>
                  </div>
                  <div class="pf-template-grid">
                    <button type="button" class="pf-template-card" *ngFor="let template of policyTemplates" (click)="aplicarPlantillaPolitica(template.id)">
                      <span class="pf-template-icon"><i class="bi" [class]="'bi ' + template.icon"></i></span>
                      <span><strong>{{ template.name }}</strong><small>{{ template.description }}</small><em>{{ template.sections }} secciones</em></span>
                      <i class="bi bi-arrow-right pf-template-arrow" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>

                <div class="form-group pf-field">
                <app-rich-text-editor
                  id="content"
                  formControlName="content"
                  [invalid]="!!(policyForm.get('content')?.invalid && policyForm.get('content')?.touched)"
                  [maxLength]="20000"
                  placeholder="Comienza explicando qué información recopila tu negocio y cómo la utiliza..."
                ></app-rich-text-editor>
                <small *ngIf="policyForm.get('content')?.invalid && policyForm.get('content')?.touched" class="error">
                  {{ policyForm.get('content')?.hasError('maxlength') ? 'El contenido no puede superar 20,000 caracteres.' : 'Escribe el contenido de la política.' }}
                </small>
                </div>
              </section>
            </div>

            <div class="form-actions pf-actions">
              <span class="pf-actions-note"><i class="bi bi-shield-check" aria-hidden="true"></i> Se mostrará a tus clientes al reservar{{ policyForm.get('is_active')?.value ? '' : ' cuando la actives' }}.</span>
              <div class="pf-actions-buttons">
                <button type="button" class="btn-cancel" (click)="solicitarCerrarFormularioPolitica()" [disabled]="isSavingPolicy()">Cancelar</button>
                <button type="submit" class="btn-save" [disabled]="isSavingPolicy()" cooldownBubble data-cooldown-key="config-save-policy">
                  <i class="bi" [class.bi-check2-circle]="!isSavingPolicy()" [class.bi-arrow-repeat]="isSavingPolicy()" aria-hidden="true"></i>
                  {{ isSavingPolicy() ? 'Guardando...' : (editingPolicy() ? 'Guardar cambios' : 'Crear política') }}
                </button>
              </div>
            </div>
          </form>
          </div>

          <!-- Modal para ver política -->
          <div *ngIf="viewingPolicy()" class="policy-modal-overlay" (click)="cerrarModalPolitica()">
            <div class="policy-modal-content" role="dialog" aria-modal="true" [attr.aria-label]="viewingPolicy()?.title" (click)="$event.stopPropagation()">
              <div class="policy-modal-header">
                <h3>{{ viewingPolicy()?.title }}</h3>
                <button (click)="cerrarModalPolitica()" class="policy-modal-close" aria-label="Cerrar"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
              </div>
              <div class="policy-modal-body">
                <div class="policy-info">
                  <p><strong>Versión:</strong> {{ viewingPolicy()?.version || 'N/A' }}</p>
                  <p><strong>Estado:</strong>
                    <span [class]="'status-' + (viewingPolicy()?.is_active ? 'active' : 'inactive')">
                      {{ viewingPolicy()?.is_active ? 'Activa' : 'Inactiva' }}
                    </span>
                  </p>
                  <p><strong>Fecha Creación:</strong> {{ formatDate(viewingPolicy()?.created_at) }}</p>
                  <p><strong>Última Actualización:</strong> {{ formatDate(viewingPolicy()?.updated_at) }}</p>
                </div>
                <div class="policy-content" [innerHTML]="viewingPolicy()?.content"></div>
              </div>
              <div class="policy-modal-footer">
                <button (click)="cerrarModalPolitica()" class="btn-cancel">Cerrar</button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  `,
  styles: [`
    .configuracion-container {
      width: 100%;
    }

    .configuracion-header {
      margin-bottom: 30px;
    }

    .configuracion-header h2 {
      margin: 0;
      font-size: 24px;
      font-weight: 700;
      color: var(--ts-text-gray-800);
    }

    .configuracion-content {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .config-section {
      background: white;
      border-radius: 12px;
      padding: 32px;
      box-shadow: 0 2px 8px var(--ts-shadow-gray-1000-a10);
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid var(--ts-border-gray-100);
    }

    .section-header h3 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      color: var(--ts-text-gray-800);
    }

    .btn-edit {
      background: var(--ts-bg);
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .btn-edit:hover {
      background: var(--ts-bg-gray-800);
    }

    .btn-cancel {
      background: white;
      color: var(--ts-text-gray-800);
      border: 2px solid var(--ts-border-gray-100);
      padding: 8px 16px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      border-color: var(--ts-border-gray-400);
      background: var(--ts-bg-gray-0-2);
    }

    .password-rule-list{display:flex;flex-wrap:wrap;gap:.35rem;margin-top:.55rem}.password-rule-list span{padding:.27rem .45rem;color:var(--ts-text-sky-450);border:1px solid var(--ts-border-navy-750-2);border-radius:999px;background:var(--ts-bg-navy-850-3);font-size:.62rem;font-weight:750}.password-rule-list span.ok{color:var(--ts-text-teal-350);border-color:var(--ts-border-teal-500-a36);background:var(--ts-bg-teal-650-a11)}.password-match-state{display:block;margin-top:.45rem;color:var(--ts-text-red-250-2)}.password-match-state.ok{color:var(--ts-text-teal-350)}
    .password-form {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }


    .form-group {
      display: flex;
      flex-direction: column;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
      color: var(--ts-text-gray-800);
      font-size: 14px;
    }

    .form-control {
      width: 100%;
      padding: 10px 14px;
      border: 2px solid var(--ts-border-gray-100);
      border-radius: 6px;
      font-size: 14px;
      box-sizing: border-box;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-control:focus {
      outline: none;
      border-color: var(--ts-bg);
    }

    .error {
      display: block;
      color: var(--ts-text-red-400);
      font-size: 12px;
      margin-top: 4px;
    }

    .form-control.ng-invalid.ng-touched {
      border-color: var(--ts-border-red-350-2) !important;
      box-shadow: 0 0 0 3px var(--ts-shadow-red-350-a12);
    }
    .password-visibility-field{position:relative;display:block}
    .password-visibility-field .form-control{padding-right:3rem}
    .password-visibility-btn{position:absolute;top:0;bottom:0;right:.45rem;display:grid;place-items:center;width:36px;height:36px;margin:auto 0;padding:0;color:var(--ts-text-sky-400);border:0;border-radius:8px;background:transparent;cursor:pointer}
    .password-visibility-btn:hover,.password-visibility-btn:focus-visible{color:var(--ts-text-blue-250-2);background:var(--ts-bg-blue-400-a12);outline:none}

    .hint {
      display: block;
      color: var(--ts-text-gray-600);
      font-size: 12px;
      margin-top: 8px;
      font-style: italic;
    }

    .form-row {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 16px;
    }

    .form-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 8px;
      padding-top: 24px;
      border-top: 2px solid var(--ts-border-gray-100);
    }

    .btn-save {
      background: var(--ts-bg);
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.3s ease;
    }

    .btn-save:hover:not(:disabled) {
      background: var(--ts-bg-gray-800);
    }

    .btn-save:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .logo-section {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .logo-preview {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 200px;
      height: 200px;
      border: 2px dashed var(--ts-border-gray-100);
      border-radius: 12px;
      overflow: hidden;
      background: var(--ts-bg-gray-0-2);
    }

    .logo-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    .no-logo {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: var(--ts-text-gray-400);
    }

    .no-logo svg {
      stroke-width: 1.5;
    }

    .logo-upload {
      display: flex;
      gap: 12px;
      align-items: center;
    }

    .file-input {
      display: none;
    }

    .file-label {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 20px;
      background: var(--ts-bg);
      color: white;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .file-label:hover {
      background: var(--ts-bg-gray-800);
    }

    .btn-upload {
      background: var(--ts-bg-green-550);
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .btn-upload:hover:not(:disabled) {
      background: var(--ts-bg-green-650);
    }

    .btn-upload:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .btn-cancel-small {
      background: white;
      color: var(--ts-text-gray-800);
      border: 2px solid var(--ts-border-gray-100);
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      font-size: 14px;
      transition: all 0.3s ease;
    }

    .btn-cancel-small:hover {
      border-color: var(--ts-border-gray-400);
      background: var(--ts-bg-gray-0-2);
    }

    /* Estilos para Políticas de Privacidad */
    .policies-list {
      padding: 20px 0;
    }

    .policies-table-container {
      width: 100%;
      max-width: 100%;
      overflow: visible;
    }

    .policies-table {
      width: 100%;
      max-width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }

    .policy-col-title{width:14%}
    .policy-col-version{width:9%}
    .policy-col-status{width:12%}
    .policy-col-date{width:20%}
    .policy-col-actions{width:25%}

    .policies-table th {
      background: var(--ts-bg-gray-50);
      padding: 12px;
      text-align: left;
      font-weight: 600;
      color: var(--ts-text-gray-800);
      border-bottom: 2px solid var(--ts-border-gray-100);
      font-size: 13px;
      overflow-wrap:anywhere;
    }

    .policies-table td {
      padding: 12px;
      border-bottom: 1px solid var(--ts-border-gray-100);
      font-size: 13px;
      overflow-wrap:anywhere;
      white-space:normal;
      vertical-align:middle;
    }

    .policies-table tr:hover {
      background: var(--ts-bg-gray-0-2);
    }

    .section-header.privacy-policy-header{margin:1.5rem -1.25rem 1.1rem}
    .policies-table td.actions-cell{padding-top:8px;padding-bottom:8px;text-align:center;vertical-align:middle}
    .policy-actions{display:flex;align-items:center;justify-content:center;gap:8px;flex-wrap:wrap;width:100%;min-width:0;min-height:48px;vertical-align:middle}
    /* styles.css normaliza globalmente .actions-cell como flex. En una tabla
       eso rompe el cálculo de altura de la fila y hace que los botones invadan
       la siguiente. En escritorio debe seguir siendo una celda real; el modo
       tarjeta de <=900px conserva su display:grid global. */
    @media(min-width:901px){.policies-table td.actions-cell{display:table-cell!important}}

    .btn-action {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.3s ease;
    }
    .btn-action i { display: block; width: 1em; height: 1em; line-height: 1; }

    .btn-view {
      background: var(--ts-bg-blue-400);
      color: white;
    }

    .btn-view:hover {
      background: var(--ts-bg-blue-450);
    }

    .btn-edit-small {
      border: 1px solid var(--ts-border-blue-400-4);
      background: var(--ts-bg-blue-400-a12);
      color: var(--ts-text-blue-300-2);
    }

    .btn-edit-small:hover {
      background: var(--ts-bg-blue-400);
      color: var(--ts-on-primary);
    }

    .btn-activate {
      background: var(--ts-bg-green-550);
      color: white;
    }

    .btn-activate:hover {
      background: var(--ts-bg-green-650);
    }

    .btn-deactivate {
      background: var(--ts-bg-orange-500);
      color: white;
    }

    .btn-deactivate:hover {
      background: var(--ts-bg-orange-550);
    }

    .btn-delete {
      background: var(--ts-bg-red-400);
      color: white;
    }

    .btn-delete:hover {
      background: var(--ts-bg-red-500);
    }

    .status-active,
    .status-inactive {
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      display: inline-block;
    }

    .policy-form {
      display: flex;
      flex-direction: column;
      gap: 20px;
      width: 100%;
      min-width: 0;
    }

    .policy-form > *,
    .policy-form .form-group,
    .policy-form .form-row,
    .policy-form .form-actions {
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
      max-width: 100%;
    }

    .content-textarea {
      font-family: 'Courier New', monospace;
      font-size: 13px;
      resize: vertical;
    }

    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      font-weight: 500;
    }

    .checkbox-input {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    /* Modal para ver política -- todo bajo el prefijo "policy-" a propósito:
       antes el header/body/footer/botón de cerrar usaban las clases
       genéricas .modal-header/.modal-body/.modal-footer/.btn-close-modal,
       que styles.css (global) sobreescribe con !important para OTROS
       modales del panel (fondo, color) -- esas reglas ganaban siempre,
       sin importar lo que este componente declarara, y encima el color de
       texto aquí seguía siendo el original de tema claro (#333/#666 sobre
       fondo blanco), pensado antes de que el modal pasara a fondo oscuro.
       Resultado: título y texto con bajo contraste y colores que no
       coincidían con el resto del panel ("se ve deformado"). Se unifica
       todo en un solo bloque, ya consistente con el tema oscuro, y con
       nombres de clase que ningún estilo global puede tocar por accidente. */
    .policy-modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      padding: 20px 24px;
      border-bottom: 1px solid var(--ts-border-strong);
      background: var(--ts-bg-navy-850-4);
    }

    .policy-modal-header h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      color: var(--ts-text-blue-0);
    }

    .policy-modal-close {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      padding: 0;
      color: var(--ts-text-sky-200-2);
      background: var(--ts-bg-navy-800-2);
      border: 1px solid var(--ts-border-navy-650-2);
      border-radius: 10px;
      font-size: 20px;
      line-height: 1;
      cursor: pointer;
      transition: color .2s ease, border-color .2s ease;
    }

    .policy-modal-close:hover {
      color: var(--ts-text-gray-0);
      border-color: var(--ts-border-sky-550-2);
    }

    .policy-modal-body {
      padding: 24px;
      overflow-y: auto;
      flex: 1;
    }

    .policy-info {
      margin-bottom: 20px;
      padding-bottom: 20px;
      border-bottom: 1px solid var(--ts-border-strong);
    }

    .policy-info p {
      margin: 8px 0;
      font-size: 14px;
      color: var(--ts-text-sky-200-3);
    }

    .policy-info strong {
      color: var(--ts-text-soft-2);
      font-weight: 600;
    }

    .policy-content {
      line-height: 1.6;
      color: var(--ts-text-sky-200-3);
    }

    .policy-content h1,
    .policy-content h2,
    .policy-content h3 {
      margin-top: 24px;
      margin-bottom: 12px;
      color: var(--ts-text-soft-2);
    }

    .policy-content h1 {
      font-size: 24px;
    }

    .policy-content h2 {
      font-size: 20px;
    }

    .policy-content h3 {
      font-size: 18px;
    }

    .policy-content p {
      margin-bottom: 12px;
    }

    .policy-content ul,
    .policy-content ol {
      margin-bottom: 12px;
      padding-left: 24px;
    }

    .policy-content li {
      margin-bottom: 6px;
    }

    .policy-modal-footer {
      padding: 18px 24px;
      border-top: 1px solid var(--ts-border-strong);
      background: var(--ts-bg-navy-850-4);
      display: flex;
      justify-content: flex-end;
    }
    /* Tema TuSala: acciones coherentes con el sistema principal. */
    .btn-edit {
      color:var(--ts-on-primary);
      background: linear-gradient(135deg, var(--ts-primary), var(--ts-bg-indigo-350));
      border: 1px solid var(--ts-primary-border);
      box-shadow: 0 8px 20px var(--ts-shadow-blue-500-a20);
    }
    .btn-edit:hover {
      background: linear-gradient(135deg, var(--ts-bg-blue-350), var(--ts-bg-indigo-350-2));
      border-color: var(--ts-border-blue-250-a70);
    }
    /* Superficie única del módulo; conserva toda la funcionalidad existente. */
    .configuracion-container{color:var(--ts-text);background:transparent}
    .configuracion-content{display:grid;gap:1rem}
    .config-hero{display:flex;justify-content:space-between;gap:1rem;align-items:flex-start;padding:1.1rem 1.2rem;border:1px solid var(--ts-border-navy-700);border-radius:15px;background:radial-gradient(circle at 85% 0,var(--ts-bg-blue-400-a20),transparent 38%),linear-gradient(145deg,var(--ts-bg-navy-850-5),var(--ts-bg-navy-900))}
    .config-hero h2{margin:.35rem 0 .45rem;font-size:1.2rem;color:var(--ts-text-blue-0-2)}
    .config-hero p{margin:0;color:var(--ts-text-sky-300);max-width:58ch}
    .hero-kicker{font-size:.68rem;font-weight:800;letter-spacing:.11em;color:var(--ts-text-blue-250)}
    .hero-badges{display:grid;gap:.45rem}
    .hero-badges span{display:inline-flex;align-items:center;gap:.4rem;padding:.45rem .6rem;border-radius:999px;border:1px solid var(--ts-border-blue-300-a34);background:var(--ts-primary-soft-07);font-size:.72rem;color:var(--ts-text-blue-100);white-space:nowrap}
    /* fill-mode "backwards" (no "forwards"): evita que translateY(0,) quede
       fijo en el panel una vez termina la animación de entrada. Un
       transform "vivo" -- aunque sea 0 -- convierte el panel en el
       "containing block" de cualquier position:fixed hijo (el modal de
       política) y le crea su propio contexto de apilamiento, aislando su
       z-index del resto de la página: por eso el botón activo de la
       pestaña ("Políticas del negocio", con z-index:1 en admin-tabs.
       component.ts) terminaba pintándose ENCIMA del modal sin importar
       qué z-index tuviera éste. */
    .tab-panel{animation:tabPanelIn .22s ease-out backwards}
    @keyframes tabPanelIn{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}
    .context-access-card{display:grid;grid-template-columns:44px minmax(0,1fr) auto;align-items:center;gap:.9rem;padding:.25rem 0}
    .context-access-icon{display:grid;place-items:center;width:44px;height:44px;border-radius:12px;color:var(--ts-warning);background:color-mix(in srgb,var(--ts-warning) 14%,transparent);border:1px solid color-mix(in srgb,var(--ts-warning) 35%,transparent);font-size:1.15rem}
    .context-access-card strong{display:block;color:var(--ts-text);font-size:.9rem}
    .context-access-card small{display:block;color:var(--ts-text-muted);font-size:.74rem;overflow-wrap:anywhere}
    .context-access-card p{margin:.45rem 0 0;color:var(--ts-text-muted);font-size:.74rem;line-height:1.5}
    .context-access-card .btn-edit{display:inline-flex;align-items:center;gap:.4rem;white-space:nowrap}
    .readonly-field{opacity:.75;cursor:not-allowed}
    @media(max-width:620px){.context-access-card{grid-template-columns:40px minmax(0,1fr)}.context-access-card .btn-edit{grid-column:1/-1;justify-content:center;width:100%}}
    .config-section{position:relative;overflow:visible;padding:1.25rem;color:var(--ts-text);background:radial-gradient(circle at 90% 0,var(--ts-bg-blue-350-a12),transparent 34%),var(--ts-bg-elevated);border:1px solid var(--ts-border-strong);border-radius:15px;box-shadow:0 10px 24px var(--ts-shadow-gray-1000-a15);transition:border-color .2s ease,box-shadow .2s ease}
    .config-section::before{content:'';position:absolute;inset:0 0 auto 0;height:3px;background:linear-gradient(90deg,var(--ts-primary),var(--ts-bg-blue-300),var(--ts-bg-indigo-300))}
    .config-section:hover{border-color:var(--ts-border-sky-600);box-shadow:0 16px 34px var(--ts-shadow-gray-1000-a22)}
    .section-header{margin:-1.25rem -1.25rem 1.1rem;padding:1rem 1.25rem;background:var(--ts-bg-elevated);border-bottom:1px solid var(--ts-border-strong)}
    .section-header h3{display:inline-flex;align-items:center;gap:.5rem;color:var(--ts-text-on-accent-3);font-size:1rem}
    .section-header h3 i{display:grid;place-items:center;width:28px;height:28px;border-radius:8px;color:var(--ts-text-blue-200);background:var(--ts-bg-blue-400-a16);border:1px solid var(--ts-border-blue-250-a28)}
    .section-title-copy{min-width:0}.section-title-copy p{max-width:760px;margin:.35rem 0 0;color:var(--ts-text-sky-350-2);font-size:.72rem;line-height:1.5}
    .policy-guide{display:grid;grid-template-columns:42px minmax(0,1fr) auto;align-items:center;gap:.85rem;margin-bottom:1rem;padding:.9rem;border:1px solid var(--ts-border-blue-400-a30);border-radius:12px;background:linear-gradient(120deg,var(--ts-bg-blue-400-a12),var(--ts-bg-navy-850-a72))}.policy-guide>i{display:grid;place-items:center;width:42px;height:42px;color:var(--ts-text-blue-250-2);border-radius:11px;background:var(--ts-primary-soft-15);font-size:1.15rem}.policy-guide strong,.policy-guide span{display:block}.policy-guide strong{color:var(--ts-text-blue-50);font-size:.8rem}.policy-guide>div>span{margin-top:.2rem;color:var(--ts-text-sky-300-2);font-size:.69rem;line-height:1.5}.policy-guide-steps{display:flex;gap:.45rem;flex-wrap:wrap;justify-content:flex-end}.policy-guide-steps span{display:inline-flex!important;align-items:center;gap:.35rem;margin:0!important;padding:.35rem .5rem;color:var(--ts-text-sky-150-6)!important;border:1px solid var(--ts-border-sky-350-a23);border-radius:999px;background:var(--ts-bg-navy-900-a34);white-space:nowrap}.policy-guide-steps b{display:grid;place-items:center;width:18px;height:18px;color:var(--ts-text);border-radius:50%;background:var(--ts-bg-blue-450-2);font-size:.58rem}
    .policies-list{padding:0}
    .profile-edit-layout{display:grid;gap:1rem}
    .profile-form-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem}
    .field-name{max-width:none}
    .locked-email{display:grid;grid-template-columns:36px minmax(0,1fr);align-items:center;gap:.7rem;min-width:0;padding:.72rem .8rem;border:1px solid var(--ts-divider);border-radius:9px;background:var(--ts-scrollbar-track)}
    .locked-email-icon{display:grid;place-items:center;width:36px;height:36px;color:var(--ts-text-sky-300-3);border-radius:9px;background:var(--ts-bg-navy-850-5)}
    .link-to-mi-sitio{display:inline;min-height:0!important;padding:0;font:inherit;border:0;background:none;color:var(--ts-text-blue-300-2);font-weight:600;text-decoration:underline;cursor:pointer}.link-to-mi-sitio:hover{color:var(--ts-text-blue-200-2)}
    .logo-section,.profile-form,.password-form,.policy-form{padding:.15rem 0}
    .logo-preview{background:var(--ts-scrollbar-track);border-color:var(--ts-divider)}
    .no-logo,.hint{color:var(--ts-text-sky-350-4)}
    .form-group label,.checkbox-label{color:var(--ts-text-sky-150-7)}
    .form-control{color:var(--ts-text-soft-2);background:var(--ts-scrollbar-track);border-color:var(--ts-divider)}
    .form-actions{border-color:var(--ts-border-strong)}
    .profile-edit-layout .form-actions{align-items:center;margin-top:.15rem;padding-top:1rem}
    .profile-edit-layout .btn-save,.profile-edit-layout .btn-cancel{display:inline-flex;align-items:center;justify-content:center;gap:.4rem;min-width:150px;min-height:42px}
    .editing-badge{display:inline-flex;align-items:center;gap:.4rem;padding:.42rem .62rem;color:var(--ts-text-blue-200);border:1px solid var(--ts-border-blue-250-a28);border-radius:999px;background:var(--ts-bg-blue-400-a12);font-size:.67rem;font-weight:700}
    .policies-table-container{border:1px solid var(--ts-border-strong);border-radius:10px}
    .policies-table th{color:var(--ts-text-sky-200-3);background:var(--ts-bg-navy-800);border-color:var(--ts-border-strong)}
    .policies-table td{color:var(--ts-text-sky-50-2);border-color:var(--ts-border-strong)}
    .policies-table tr:hover{background:var(--ts-header-wash-2)}
    .empty-state{color:var(--ts-text-blue-300);background:var(--ts-bg-navy-850-2);border-color:var(--ts-border-strong)}
    .policy-modal-overlay{position:fixed!important;z-index:6000!important;inset:0!important;display:grid!important;place-items:center!important;padding:clamp(12px,3vw,28px)!important;background:var(--ts-bg-navy-950-a80)!important;backdrop-filter:blur(9px)}
    .policy-modal-content{display:flex;flex-direction:column;box-sizing:border-box;width:min(900px,100%);max-height:calc(100dvh - 28px);overflow:hidden;color:var(--ts-text);background:var(--ts-bg-elevated);border:1px solid var(--ts-border-sky-650);border-radius:18px;box-shadow:0 30px 90px var(--ts-shadow-gray-1000-a55)}
    .settings-modal-overlay{position:fixed;z-index:6000;inset:0;display:grid;place-items:center;padding:clamp(12px,3vw,28px);background:var(--ts-bg-navy-950-a82-2);backdrop-filter:blur(9px)}
    .settings-modal-content{display:flex!important;flex-direction:column;box-sizing:border-box;width:min(820px,100%);max-height:calc(100dvh - 28px);margin:0!important;padding:0!important;overflow:hidden;color:var(--ts-text);background:var(--ts-bg-elevated);border:1px solid var(--ts-border-sky-650);border-radius:18px;box-shadow:0 30px 90px var(--ts-shadow-gray-1000-a55)}
    .settings-modal-header{display:flex;align-items:center;justify-content:space-between;gap:1rem;padding:1rem 1.2rem;border-bottom:1px solid var(--ts-border-strong);background:linear-gradient(120deg,var(--ts-bg-blue-400-a14),var(--ts-bg-navy-850-a92))}
    .settings-modal-header h3{margin:.15rem 0 0;color:var(--ts-text-blue-0-2);font-size:1.05rem}.modal-kicker{color:var(--ts-text-sky-300-4);font-size:.62rem;font-weight:800;letter-spacing:.11em}
    .settings-modal-body{display:flex;flex-direction:column;gap:1rem;padding:1.2rem;overflow:auto;overscroll-behavior:contain}
    .settings-modal-content>.form-actions{flex:none;margin:0;padding:1rem 1.2rem;border-top:1px solid var(--ts-border-strong);background:var(--ts-bg-navy-850-6)}
    .settings-modal-content.policy-form .form-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(220px,.7fr);gap:1rem}
    body:has(.settings-modal-overlay),body:has(.policy-modal-overlay){overflow:hidden}
    @media(max-width:960px){.policy-guide{grid-template-columns:42px minmax(0,1fr)}.policy-guide-steps{grid-column:1/-1;justify-content:flex-start}}
    @media(max-width:780px){.config-hero{flex-direction:column}.hero-badges{grid-template-columns:repeat(3,minmax(0,1fr));width:100%}.hero-badges span{justify-content:center}.profile-form-grid{grid-template-columns:1fr}}
    @media(max-width:600px){.settings-modal-overlay{padding:0;place-items:stretch}.settings-modal-content{width:100%;max-height:100dvh;border-radius:0}.settings-modal-content.policy-form .form-row{grid-template-columns:1fr}.settings-modal-content>.form-actions{display:grid;grid-template-columns:1fr 1fr}.content-textarea{width:100%;min-height:220px!important}}
    @media(max-width:520px){.config-section{padding:1rem}.section-header{align-items:flex-start;gap:.7rem;margin:-1rem -1rem 1rem;padding:.9rem 1rem}.section-header h3{font-size:.94rem}.section-title-copy{width:100%}.section-title-copy p{max-width:100%}.locked-email{grid-template-columns:32px minmax(0,1fr)}.locked-email-icon{width:32px;height:32px}.profile-edit-layout .form-actions{display:grid;grid-template-columns:1fr;width:100%}.profile-edit-layout .btn-save,.profile-edit-layout .btn-cancel{width:100%}}

    /* ---------- Modal de crear/editar política ---------- */
    .pf-modal{width:min(1120px,96vw)!important;max-height:min(960px,calc(100dvh - 20px))}
    .pf-header{justify-content:flex-start;gap:.85rem;padding:1rem 1.2rem}
    .pf-badge{display:grid;place-items:center;flex:0 0 44px;width:44px;height:44px;color:var(--ts-text-on-accent);border-radius:13px;background:linear-gradient(135deg,var(--ts-primary),var(--ts-indigo,#6366f1));box-shadow:0 10px 24px var(--ts-shadow-blue-400-a32);font-size:1.15rem}
    .pf-heading{flex:1;min-width:0}
    .pf-heading p{margin:.2rem 0 0;color:var(--ts-text-sky-300-2);font-size:.74rem;line-height:1.4}
    .pf-header .policy-modal-close{flex:0 0 auto;align-self:flex-start}
    .pf-body{gap:.8rem;padding:1rem 1.15rem 1.1rem;background:radial-gradient(120% 60% at 100% 0,var(--ts-bg-blue-400-a7),transparent 60%)}
    .pf-section{display:grid;gap:.9rem;padding:1rem;border:1px solid var(--ts-border-strong);border-radius:14px;background:var(--ts-bg-navy-900-a55)}
    .pf-unified{position:relative;gap:.75rem;padding:.9rem 1rem 1rem}.pf-section-divider{height:1px;margin:.05rem 0;background:var(--ts-divider)}.pf-content-head{margin-top:.05rem}
    .pf-section-head{display:flex;align-items:center;gap:.7rem}
    .pf-section-head>div{flex:1;min-width:0}
    .pf-section-head h4{margin:0;color:var(--ts-text-on-accent-4);font-size:.86rem;font-weight:800}
    .pf-section-head small{display:block;margin-top:.1rem;color:var(--ts-text-sky-350-3);font-size:.7rem}
    .pf-step{display:grid;place-items:center;flex:0 0 26px;width:26px;height:26px;color:var(--ts-step-text, var(--ts-text-on-accent-5));border:1px solid var(--ts-border-blue-250-a40);border-radius:50%;background:var(--ts-bg-blue-400-a16);font-size:.72rem;font-weight:800}
    .pf-req{color:var(--ts-text-red-250-3);font-style:normal}
    .pf-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(140px,190px);gap:.9rem}
    .pf-grid-single{grid-template-columns:1fr}
    .pf-general-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(280px,360px);align-items:end;gap:.9rem}
    .pf-field{display:grid;align-content:start;gap:.4rem;margin:0}
    .pf-label{display:flex!important;align-items:baseline;gap:.35rem;width:100%;margin:0!important;color:var(--ts-text-sky-150-7);font-size:.76rem;font-weight:700}
    .pf-optional{color:var(--ts-text-sky-400);font-size:.66rem;font-weight:600}
    .pf-count{margin-left:auto;color:var(--ts-text-sky-400);font-size:.66rem;font-weight:700;font-variant-numeric:tabular-nums}
    .pf-count.warn{color:var(--ts-text-amber-450)}
    .pf-field .form-control{box-sizing:border-box;width:100%;min-height:44px;border-radius:11px;font-size:.86rem;transition:border-color .2s ease,box-shadow .2s ease}
    .pf-field .form-control:focus{border-color:var(--ts-primary);box-shadow:0 0 0 3px var(--ts-shadow-blue-400-a22);outline:none}
    .pf-input-icon{position:relative;display:block}
    .pf-input-icon>i{position:absolute;top:22px;left:.85rem;z-index:1;color:var(--ts-text-sky-400);line-height:1;transform:translateY(-50%);pointer-events:none}
    .pf-input-icon .form-control{padding-left:2.35rem!important}

    .pf-activate{position:relative;display:flex;align-items:center;gap:.8rem;margin:0;padding:.75rem .9rem;border:1px solid var(--ts-border-strong);border-radius:12px;background:var(--ts-scrollbar-track);cursor:pointer;transition:border-color .2s ease,background .2s ease}
    .pf-activate:hover{border-color:var(--ts-border-blue-250-a50)}
    .pf-activate.on{border-color:var(--ts-border-green-550-a50);background:var(--ts-bg-green-550-a8)}
    .pf-activate-input{position:absolute;width:1px;height:1px;opacity:0;pointer-events:none}
    .pf-switch{position:relative;flex:0 0 40px;width:40px;height:22px;border-radius:999px;background:var(--ts-bg-navy-700);transition:background .2s ease}
    .pf-switch span{position:absolute;top:3px;left:3px;width:16px;height:16px;border-radius:50%;background:var(--ts-bg-gray-0);box-shadow:0 1px 3px var(--ts-shadow-gray-1000-a35);transition:transform .22s cubic-bezier(.2,.8,.2,1)}
    .pf-activate.on .pf-switch{background:var(--ts-bg-green-550)}
    .pf-activate.on .pf-switch span{transform:translateX(18px)}
    .pf-activate-input:focus-visible+.pf-switch{outline:3px solid var(--ts-border-blue-400-a45);outline-offset:2px}
    .pf-activate-text{display:grid;flex:1;min-width:0;gap:.1rem}
    .pf-activate-text strong{color:var(--ts-text-soft-2);font-size:.8rem}
    .pf-activate-text small{color:var(--ts-text-sky-350-3);font-size:.7rem;line-height:1.35}
    .pf-activate-icon{color:var(--ts-text-sky-400);font-size:1.1rem}
    .pf-activate.on .pf-activate-icon{color:var(--ts-text-green-400)}

    .pf-template-btn{display:inline-flex;align-items:center;gap:.4rem;flex:0 0 auto;min-height:36px;padding:0 .8rem;color:var(--ts-step-text, var(--ts-text-on-accent-5));border:1px solid var(--ts-border-blue-250-a35);border-radius:10px;background:var(--ts-bg-blue-400-a12);font-size:.74rem;font-weight:700;cursor:pointer;transition:background .2s ease,border-color .2s ease}
    .pf-template-btn:hover{border-color:var(--ts-border-blue-250-a65);background:var(--ts-bg-blue-400-a22)}
    .pf-template-btn.active{color:#fff;border-color:#2563eb;background:#2563eb}
    .pf-template-picker{position:absolute;top:190px;right:1rem;z-index:12;display:grid;gap:.75rem;width:min(650px,calc(100% - 2rem));padding:.85rem;border:1px solid #93c5fd;border-radius:12px;background:#eff6ff;box-shadow:0 18px 44px -18px rgba(15,39,77,.55)}
    .pf-template-intro{display:flex;align-items:center;justify-content:space-between;gap:.7rem;color:#17243a}
    .pf-template-intro>div{display:grid;gap:.12rem}.pf-template-intro strong{font-size:.82rem}.pf-template-intro small{color:#405574;font-size:.7rem}.pf-template-intro>button{display:grid;place-items:center;width:30px;height:30px;padding:0;color:#315b92;border:1px solid #bfdbfe;border-radius:8px;background:#fff;cursor:pointer}
    .pf-template-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.55rem}
    .pf-template-card{display:grid;grid-template-columns:38px minmax(0,1fr) 20px;align-items:center;gap:.65rem;min-height:82px;padding:.7rem;text-align:left;color:#17243a;border:1px solid #c7d8ee;border-radius:11px;background:#fff;cursor:pointer;transition:border-color .18s,transform .18s,box-shadow .18s}
    .pf-template-card:hover{border-color:#2563eb;box-shadow:0 8px 20px -16px rgba(37,99,235,.8);transform:translateY(-1px)}
    .pf-template-icon{display:grid;place-items:center;width:38px;height:38px;color:#1d4ed8;border-radius:10px;background:#dbeafe;font-size:1rem}.pf-template-card>span:nth-child(2){display:grid;gap:.14rem}.pf-template-card strong{font-size:.76rem}.pf-template-card small{color:#405574;font-size:.66rem;line-height:1.35}.pf-template-card em{color:#1d4ed8;font-size:.61rem;font-style:normal;font-weight:800;text-transform:uppercase}.pf-template-arrow{color:#547090}
    /* El contenido insertado dinámicamente no recibe el atributo de encapsulación
       del componente; estas reglas mantienen su presentación consistente. */
    :host ::ng-deep .policy-content{color:var(--ts-text-sky-200-3);font-size:.88rem;line-height:1.65;overflow-wrap:anywhere}
    :host ::ng-deep .policy-content :is(h1,h2,h3){margin:1.15rem 0 .5rem;color:var(--ts-text-soft-2);font-weight:800;line-height:1.3;letter-spacing:-.01em}
    :host ::ng-deep .policy-content h1{font-size:1.35rem}
    :host ::ng-deep .policy-content h2{font-size:1.08rem}
    :host ::ng-deep .policy-content h3{font-size:.96rem}
    :host ::ng-deep .policy-content p{margin:0 0 .75rem}
    :host ::ng-deep .policy-content :is(ul,ol){margin:0 0 .75rem;padding-left:1.4rem}
    :host ::ng-deep .policy-content li{margin-bottom:.35rem}
    :host ::ng-deep .policy-content strong{color:var(--ts-text-soft-2)}
    :host ::ng-deep .policy-content a{color:var(--ts-text-blue-250-2);text-decoration:underline;text-underline-offset:2px}

    .pf-actions{display:flex!important;align-items:center;justify-content:space-between;gap:.8rem}
    .pf-actions-note{display:inline-flex;align-items:center;gap:.4rem;min-width:0;color:var(--ts-text-sky-350-3);font-size:.72rem}
    .pf-actions-note i{color:var(--ts-text-green-400)}
    .pf-actions-buttons{display:flex;align-items:center;gap:.6rem}
    .pf-actions-buttons>.btn-save,.pf-actions-buttons>.btn-cancel{display:inline-flex;align-items:center;justify-content:center;flex:0 0 160px;width:160px;min-width:160px;height:44px;min-height:44px;padding:.65rem 1rem;line-height:1.2}

    @media(max-width:760px){
      .pf-general-row{grid-template-columns:1fr;align-items:stretch}
      .policy-actions{width:100%;min-width:0}
      .pf-actions{flex-direction:column;align-items:stretch}
      .pf-actions-note{justify-content:center;text-align:center}
      .pf-actions-buttons{display:grid;grid-template-columns:1fr 1fr;width:100%}
      .pf-actions-buttons>.btn-save,.pf-actions-buttons>.btn-cancel{flex:1 1 0;min-width:0;width:100%}
    }
    @media(max-width:600px){
      .pf-modal{width:100%!important;max-height:100dvh!important;height:100dvh}
      .pf-header{padding:.85rem 1rem}
      .pf-badge{flex-basis:38px;width:38px;height:38px;font-size:1rem}
      .pf-heading p{display:none}
      .pf-body{padding:.9rem .85rem 1rem}
      .pf-section{padding:.85rem .8rem}
      .pf-grid{grid-template-columns:1fr}
      .section-header.privacy-policy-header{margin:1rem -1rem 1rem}
      .pf-section-head{flex-wrap:wrap}
      .pf-template-btn{width:100%;justify-content:center}
      .pf-template-grid{grid-template-columns:1fr}
      .pf-template-picker{position:absolute;top:176px;right:.8rem;left:.8rem;width:auto;max-height:calc(100dvh - 210px);overflow:auto}
      .pf-actions-note{display:none}
    }
    @media(max-width:360px){.pf-actions-buttons{grid-template-columns:1fr}}
    @media(prefers-reduced-motion:reduce){.pf-switch span,.pf-activate{transition:none}}

    /* Información e identidad: ficha de perfil a ancho completo (logo, nombre,
       propietario y datos) y seguridad debajo; sin columnas de alturas distintas. */
    .identity-layout{display:grid;gap:1rem}
    .identity-card{padding:1.2rem 1.3rem;border-radius:14px}
    .identity-card::before{display:none}
    .identity-card:hover{box-shadow:0 10px 24px var(--ts-shadow-gray-1000-a15)}
    .identity-card-head{display:flex;align-items:center;gap:.75rem;margin-bottom:1.1rem}
    .identity-card-icon{display:grid;place-items:center;flex:0 0 38px;width:38px;height:38px;border-radius:10px;color:var(--ts-text-blue-200);background:var(--ts-bg-blue-400-a16);font-size:1.05rem}
    .identity-card-title{flex:1 1 auto;min-width:0}
    .identity-card-title h3{margin:0;color:var(--ts-text);font-size:1rem;font-weight:700;line-height:1.3}
    .identity-card-title p{margin:.15rem 0 0;font-size:.74rem;line-height:1.45}
    .identity-action{display:inline-flex;align-items:center;justify-content:center;gap:.4rem;flex:0 0 auto;white-space:nowrap}

    .identity-profile{display:grid;grid-template-columns:112px minmax(0,1fr) minmax(220px,auto);align-items:center;gap:1.25rem;padding:1rem;border-radius:12px;background:var(--ts-primary-soft-055)}
    .identity-profile .logo-section{padding:0}
    .identity-profile .logo-preview{width:112px;height:112px;border:1px dashed var(--ts-divider);border-radius:16px;background:var(--ts-bg-elevated)}
    .identity-profile .logo-preview.has-logo{border-style:solid}
    .identity-profile .logo-image{width:100%;height:100%;padding:.45rem;box-sizing:border-box;object-fit:contain}
    .identity-profile .no-logo{gap:.25rem;padding:.3rem;text-align:center}
    .no-logo-initials{color:var(--ts-text-blue-200);font-size:1.75rem;font-weight:800;line-height:1}
    .identity-profile .no-logo small{font-size:.62rem;line-height:1.2}
    .identity-profile-main{display:grid;justify-items:start;gap:.3rem;min-width:0}
    .identity-kicker{color:var(--ts-text-muted);font-size:.66rem;font-weight:700;text-transform:uppercase;letter-spacing:.06em}
    .identity-name{max-width:100%;overflow-wrap:anywhere;color:var(--ts-text);font-size:1.35rem;font-weight:800;line-height:1.2;letter-spacing:-.01em}
    .identity-owner-chip{display:inline-flex;align-items:center;gap:.45rem;margin-top:.25rem;padding:.25rem .65rem .25rem .25rem;border:1px solid var(--ts-divider);border-radius:999px;background:var(--ts-bg-elevated);color:var(--ts-text-muted);font-size:.74rem}
    .identity-owner-chip b{color:var(--ts-text);font-weight:700}
    .identity-owner-avatar{display:grid;place-items:center;flex:0 0 24px;width:24px;height:24px;border-radius:50%;color:#fff;background:var(--ts-primary);font-size:.6rem;font-weight:700}
    .identity-profile .logo-upload{display:grid;justify-items:end;gap:.45rem;min-width:0}
    .logo-file-name{max-width:100%;overflow:hidden;color:var(--ts-text);font-size:.76rem;text-overflow:ellipsis;white-space:nowrap}
    .logo-actions{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:.5rem}
    .identity-profile :is(.file-label,.btn-upload,.btn-cancel-small){display:inline-flex;align-items:center;justify-content:center;gap:.4rem;min-height:38px;padding:.5rem .95rem;border-radius:9px;font-size:.8rem;font-weight:600}
    .identity-profile .file-label{background:var(--ts-primary)}
    .identity-profile .file-label,.identity-profile .file-label i{color:#fff!important}
    .identity-profile .file-label:hover{filter:brightness(1.08)}
    .identity-profile .btn-cancel-small{color:var(--ts-text);background:transparent;border:1px solid var(--ts-divider)}
    .identity-profile .hint{margin:0;font-size:.66rem;line-height:1.4;text-align:right}

    .identity-facts{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));margin:1.1rem 0 0}
    .identity-fact{display:grid;align-content:start;gap:.3rem;min-width:0;padding:.15rem 1.1rem;border-left:1px solid var(--ts-divider)}
    .identity-fact:first-child{padding-left:0;border-left:0}
    .identity-fact-label{display:flex;align-items:center;gap:.4rem;color:var(--ts-text-muted);font-size:.7rem;font-weight:600}
    .identity-fact-label i{color:var(--ts-text-blue-250-2);font-size:.8rem}
    .identity-fact-value{display:block;margin:0;overflow-wrap:anywhere;color:var(--ts-text);font-size:.88rem;font-weight:600}
    .identity-fact-value a{color:var(--ts-text-blue-250-2);text-decoration:none}.identity-fact-value a:hover{text-decoration:underline}
    .identity-fact-link{justify-self:start;font-size:.68rem!important}
    .identity-empty{color:var(--ts-text-muted);font-weight:500;font-style:italic}

    .security-rows{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:1rem}
    .security-row{display:grid;grid-template-columns:38px minmax(0,1fr) auto;align-items:center;gap:.75rem;min-height:64px;padding:.8rem .9rem;border:1px solid var(--ts-divider);border-radius:12px}
    .security-row-icon{display:grid;place-items:center;width:38px;height:38px;border-radius:10px;color:var(--ts-text-blue-200);background:var(--ts-bg-blue-400-a16)}
    .security-row>div{display:grid;gap:.1rem;min-width:0}
    .security-row small{font-size:.68rem;font-weight:600}
    .security-row strong{overflow:hidden;color:var(--ts-text);font-size:.86rem;text-overflow:ellipsis;white-space:nowrap}

    /* Modal "Editar perfil comercial" */
    .profile-modal-header{align-items:flex-start;justify-content:flex-start}
    .profile-modal-icon{display:grid;place-items:center;flex:0 0 40px;width:40px;height:40px;border-radius:11px;color:var(--ts-text-blue-200);background:var(--ts-bg-blue-400-a16);font-size:1.05rem}
    .profile-modal-heading{flex:1 1 auto;min-width:0}
    .profile-modal-heading p{margin:.25rem 0 0;font-size:.74rem;line-height:1.4}
    .profile-modal-header .policy-modal-close{flex:0 0 auto}
    .profile-modal-body{gap:1.1rem;padding:1.25rem}
    .profile-group{display:grid;gap:.85rem;min-width:0;margin:0;padding:1rem 1.05rem 1.05rem;border:1px solid var(--ts-divider);border-radius:12px}
    .profile-group legend{display:inline-flex;align-items:center;gap:.4rem;padding:0 .4rem;margin-left:-.4rem;color:var(--ts-text);font-size:.78rem;font-weight:700}
    .profile-group legend i{color:var(--ts-text-blue-250-2)}
    .profile-modal-body .profile-form-grid{column-gap:1rem;row-gap:.9rem;align-items:start}
    .profile-modal-body .form-group{display:grid;gap:.35rem;min-width:0;margin:0}
    .profile-modal-body .form-group>label,.locked-email-label{display:flex;align-items:center;gap:.4rem;margin:0;color:var(--ts-text)!important;font-size:.76rem;font-weight:600}
    .profile-modal-body .form-control{box-sizing:border-box;width:100%;min-height:42px;border-radius:10px;font-size:.85rem}
    .profile-modal-body .hint{margin:0;font-size:.68rem;font-style:normal;line-height:1.45}
    .optional-tag{padding:.08rem .4rem;border-radius:999px;background:var(--ts-scrollbar-track);color:var(--ts-text-muted);font-size:.6rem;font-weight:600}
    .locked-email-group .locked-email{grid-template-columns:28px minmax(0,1fr);gap:.55rem;min-height:42px;padding:.35rem .7rem;border-radius:10px}
    .locked-email-group .locked-email-icon{width:28px;height:28px;border-radius:8px;font-size:.75rem}
    .locked-email-value{overflow:hidden;color:var(--ts-text-muted)!important;font-size:.85rem;font-weight:500;text-overflow:ellipsis;white-space:nowrap}

    @media(max-width:1100px){.identity-facts{grid-template-columns:repeat(2,minmax(0,1fr));row-gap:1rem}.identity-fact:nth-child(odd){padding-left:0;border-left:0}}
    @media(max-width:860px){
      .identity-profile{grid-template-columns:96px minmax(0,1fr)}
      .identity-profile .logo-preview{width:96px;height:96px}
      .identity-profile .logo-upload{grid-column:1/-1;justify-items:start}
      .logo-actions{justify-content:flex-start}
      .identity-profile .hint{text-align:left}
      .security-rows{grid-template-columns:1fr}
    }
    @media(max-width:600px){.profile-modal-body{padding:1rem}.profile-modal-body .profile-form-grid{grid-template-columns:1fr}.profile-group{padding:.85rem .85rem .95rem}.profile-modal-heading p{display:none}}
    @media(max-width:520px){
      .identity-card{padding:1rem}
      .identity-card-head{flex-wrap:wrap}
      .identity-card-title{flex-basis:calc(100% - 52px)}
      .identity-card-head .identity-action{width:100%}
      .identity-profile{grid-template-columns:72px minmax(0,1fr);gap:.85rem;padding:.85rem}
      .identity-profile .logo-preview{width:72px;height:72px;border-radius:12px}
      .no-logo-initials{font-size:1.3rem}
      .identity-profile .no-logo small{display:none}
      .identity-name{font-size:1.1rem}
      .identity-profile .logo-actions>*{flex:1 1 auto}
      .identity-facts{grid-template-columns:1fr;row-gap:0}
      .identity-fact,.identity-fact:nth-child(odd){padding:.75rem 0;border-left:0;border-top:1px solid var(--ts-divider)}
      .identity-fact:first-child{border-top:0}
      .security-row{grid-template-columns:38px minmax(0,1fr)}
      .security-row .identity-action{grid-column:1/-1;width:100%}
    }
  `]
})
export class ConfiguracionComponent implements OnInit {
  @Output() irAMiSitio = new EventEmitter<void>();

  readonly configTabs = [
    {
      id: 'negocio',
      label: 'Información e identidad',
      description: 'Perfil, logo y seguridad',
      hash: 'informacion-negocio',
      icon: 'bi-building-gear',
      tabId: 'tab-informacion-negocio',
      panelId: 'panel-informacion-negocio'
    },
    {
      id: 'politicas',
      label: 'Políticas del negocio',
      description: 'Versiones, vigencia y privacidad',
      hash: 'politicas-negocio',
      icon: 'bi-file-earmark-lock2',
      tabId: 'tab-politicas-negocio',
      panelId: 'panel-politicas-negocio'
    }
  ] as const;
  activeTab = signal<ConfigTab>('negocio');
  profileForm!: FormGroup;
  passwordForm!: FormGroup;
  policyForm!: FormGroup;
  @ViewChild('profileFormRef') profileFormRef!: ElementRef<HTMLFormElement>;
  @ViewChild('passwordFormRef') passwordFormRef!: ElementRef<HTMLFormElement>;
  @ViewChild('policyFormRef') policyFormRef!: ElementRef<HTMLFormElement>;
  showEditProfile = signal<boolean>(false);
  showChangePassword = signal<boolean>(false);
  showCurrentPassword = signal(false);
  showNewPassword = signal(false);
  showConfirmPassword = signal(false);
  showCreatePolicyForm = signal<boolean>(false);
  showPolicyTemplatePicker = signal<boolean>(false);
  readonly policyTemplates = [
    { id: 'privacy', name: 'Privacidad y datos', description: 'Explica qué información recopilas y cómo la proteges.', sections: 5, icon: 'bi-shield-lock' },
    { id: 'reservations', name: 'Reservas y uso del espacio', description: 'Reglas de ingreso, horarios, capacidad y responsabilidades.', sections: 6, icon: 'bi-calendar2-check' },
    { id: 'cancellations', name: 'Cancelaciones y reembolsos', description: 'Aclara anticipación, cargos, cambios y devoluciones.', sections: 5, icon: 'bi-arrow-counterclockwise' },
    { id: 'complete', name: 'Política integral', description: 'Una base completa que reúne privacidad, reservas y cancelaciones.', sections: 9, icon: 'bi-file-earmark-check' },
  ] as const;
  editingPolicy = signal<PrivacyPolicy | null>(null);
  viewingPolicy = signal<PrivacyPolicy | null>(null);
  selectedLogo = signal<File | null>(null);
  logoPreview = signal<string | null>(null);
  isUpdatingProfile = signal<boolean>(false);
  isUploadingLogo = signal<boolean>(false);
  isChangingPassword = signal<boolean>(false);
  isSavingPolicy = signal<boolean>(false);
  isLoadingPolicies = signal<boolean>(false);
  isLoading = signal<boolean>(false);

  private readonly LOGOS_BASE_URL = 'https://storage.googleapis.com/thehubp/business-logos/';

  private readonly router = inject(Router);

  constructor(
    private fb: FormBuilder,
    private businessConfigService: BusinessConfigService,
    private authService: AuthService,
    public privacyPolicyService: PrivacyPolicyService,
    private dialogs: DialogService,
    private notifications: NotificationService,
    private cooldown: ActionCooldownService,
  ) {}

  get currentBusiness() {
    return this.authService.currentUser;
  }

  /** El logo guardado no se pudo cargar (URL rota): se muestran las iniciales. */
  readonly logoLoadFailed = signal(false);

  showLogoImage(): boolean {
    if (this.logoPreview()) return true;
    return !!this.currentBusiness()?.logo && !this.logoLoadFailed();
  }

  private initialsOf(value: string | null | undefined, fallback: string): string {
    const words = String(value || '').trim().split(/\s+/).filter(Boolean);
    return (words.slice(0, 2).map(w => w.charAt(0)).join('') || fallback).toUpperCase();
  }

  businessInitials(): string {
    return this.initialsOf(this.currentBusiness()?.name, 'N');
  }

  ownerInitials(): string {
    return this.initialsOf(this.currentBusiness()?.owner?.name, 'P');
  }

  /** Dentro del modal "Gestionar negocio" de Super Admin. */
  get businessContextMode(): boolean {
    return this.authService.isBusinessContextMode();
  }

  manageOwnerAccount(): void {
    this.authService.requestOwnerAccountManagement();
  }

  ngOnInit(): void {
    this.syncTabFromHash(true);
    this.initProfileForm();
    this.initPasswordForm();
    this.initPolicyForm();
    this.cargarPerfil();
    this.cargarPoliticas();
  }

  selectTab(tab: string): void {
    if (tab !== 'negocio' && tab !== 'politicas') {
      return;
    }
    const selectedTab = tab as ConfigTab;
    this.activeTab.set(selectedTab);
    const selected = this.configTabs.find((item) => item.id === selectedTab);
    if (!selected || window.location.hash === `#${selected.hash}`) {
      return;
    }

    window.history.pushState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}#${selected.hash}`
    );
  }

  @HostListener('window:hashchange')
  onHashChange(): void {
    this.syncTabFromHash(false);
  }

  @HostListener('window:popstate')
  onHistoryChange(): void {
    this.syncTabFromHash(false);
  }

  @HostListener('document:keydown.escape')
  closeOpenModal(): void {
    if (this.viewingPolicy()) return this.cerrarModalPolitica();
    if (this.showPolicyTemplatePicker()) return this.showPolicyTemplatePicker.set(false);
    if (this.showCreatePolicyForm() || this.editingPolicy()) {
      void this.solicitarCerrarFormularioPolitica();
      return;
    }
    if (this.showChangePassword()) return this.cancelarCambioPassword();
    if (this.showEditProfile()) this.cancelarEdicionPerfil();
  }

  private syncTabFromHash(replaceInvalidHash: boolean): void {
    const hash = window.location.hash.replace(/^#/, '').trim().toLowerCase();
    if (hash === 'identidad-visual') {
      this.activeTab.set('negocio');
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}#informacion-negocio`
      );
      return;
    }
    const selected = this.configTabs.find((item) => item.hash === hash);
    if (selected) {
      this.activeTab.set(selected.id);
      return;
    }

    this.activeTab.set('negocio');
    if (replaceInvalidHash) {
      const defaultHash = this.configTabs[0].hash;
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}#${defaultHash}`
      );
    }
  }

  initProfileForm(): void {
    const business = this.currentBusiness();
    this.profileForm = this.fb.group({
      name: [business?.name || '', [Validators.required, Validators.minLength(2), Validators.maxLength(100), Validators.pattern(/^[^\u0000-\u001F\u007F]+$/)]],
      company: [business?.company || '', [Validators.required, Validators.minLength(2), Validators.maxLength(100), Validators.pattern(/^[^\u0000-\u001F\u007F]+$/)]],
      website: [business?.website || '', [Validators.maxLength(255), Validators.pattern(/^https?:\/\/[^\s.]+(?:\.[^\s.]+)+(?:[^\s]*)?$/i)]],
      phone: [business?.phone || '', [Validators.required, Validators.pattern(/^\+?\d{7,15}$/)]],
      email: [business?.session_user?.email || '', [Validators.required, Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)]]
    });
  }

  sanitizePhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const hasLeadingPlus = input.value.trimStart().startsWith('+');
    const digits = input.value.replace(/\D/g, '').slice(0, 15);
    const sanitized = `${hasLeadingPlus ? '+' : ''}${digits}`;
    if (input.value !== sanitized) {
      input.value = sanitized;
    }
    this.profileForm.get('phone')?.setValue(sanitized, { emitEvent: false });
    this.profileForm.get('phone')?.markAsDirty();
  }

  fieldInteracted(field: string): boolean {
    const control = this.profileForm.get(field);
    return !!control && (control.dirty || control.touched);
  }

  initPasswordForm(): void {
    this.passwordForm = this.fb.group({
      current_password: ['', Validators.required],
      new_password: ['', [Validators.required, Validators.minLength(6)]],
      confirm_password: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('new_password');
    const confirmPassword = form.get('confirm_password');
    
    if (newPassword && confirmPassword && newPassword.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    return null;
  }

  passwordRule(): boolean {
    const value = String(this.passwordForm?.get('new_password')?.value || '');
    return value.length >= 6;
  }

  passwordsMatch(): boolean {
    const next = String(this.passwordForm?.get('new_password')?.value || '');
    return !!next && next === String(this.passwordForm?.get('confirm_password')?.value || '');
  }

  initPolicyForm(): void {
    this.policyForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(255), Validators.pattern(/\S/)]],
      content: ['', [Validators.required, Validators.maxLength(20000), Validators.pattern(/\S/)]],
      is_active: [false]
    });
  }

  cargarPerfil(): void {
    const businessId = this.currentBusiness()?.id;
    if (!businessId) {
      return;
    }

    this.isLoading.set(true);
    this.businessConfigService.obtenerPerfil(businessId).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        // Actualizar el usuario en el servicio de auth
        this.authService.updateCurrentUser(response.business);
        this.initProfileForm();
        // Solo se CARGÓ el perfil (nada cambió): no se avisa "configuración
        // cambiada" -- ese aviso hacía que el onboarding volviera a pedir
        // perfil, políticas y cobros al abrir esta pantalla (consultas dobles).
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al cargar perfil:', error);
      }
    });
  }

  cancelarEdicionPerfil(): void {
    this.showEditProfile.set(false);
    this.initProfileForm();
  }

  abrirEdicionPerfil(): void {
    this.initProfileForm();
    this.showEditProfile.set(true);
  }

  async guardarPerfil(): Promise<void> {
    if (!this.cooldown.consume('config-save-profile')) return;
    const businessId = this.currentBusiness()?.id;
    if (!businessId) return;
    if (!validateAndFocusFirstError(this.profileForm, this.profileFormRef?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de guardar.')) return;

    const updateData: UpdateBusinessProfileRequest = {
      name: String(this.profileForm.get('name')?.value || '').trim(),
      company: String(this.profileForm.get('company')?.value || '').trim(),
      website: String(this.profileForm.get('website')?.value || '').trim(),
      phone: String(this.profileForm.get('phone')?.value || '').trim()
    };
    const newEmail = String(this.profileForm.get('email')?.value || '').trim().toLowerCase();
    const business = this.currentBusiness();
    const normalize = (value: unknown) => String(value || '').trim();
    const currentEmail = normalize(business?.session_user?.email).toLowerCase();
    const emailChanged = newEmail !== currentEmail;
    const otherFieldsChanged = normalize(updateData.name) !== normalize(business?.name)
      || normalize(updateData.company) !== normalize(business?.company)
      || normalize(updateData.website) !== normalize(business?.website)
      || normalize(updateData.phone) !== normalize(business?.phone);
    if (!emailChanged && !otherFieldsChanged) {
      this.notifications.error('No hay cambios por guardar en el perfil.');
      return;
    }

    this.isUpdatingProfile.set(true);

    // El correo de acceso (login) es sensible: cambiarlo exige confirmar la
    // contraseña actual del propietario en un modal aparte, antes de tocar
    // nada más. Si esto falla o se cancela, no se guarda el resto del form.
    if (emailChanged) {
      try {
        const emailResult = await this.dialogs.confirmAndRun(
          {
            title: 'Confirma tu contraseña',
            message: `Vas a cambiar el correo con el que inicias sesión a ${newEmail}. Ingresa tu contraseña actual para confirmarlo.`,
            tone: 'warning',
            confirmText: 'Confirmar correo',
            fields: [{ name: 'current_password', label: 'Contraseña actual', type: 'password', required: true, placeholder: 'Tu contraseña de inicio de sesión' }],
          },
          (values: Record<string, string | boolean>) => this.businessConfigService.cambiarCorreo(businessId, { current_password: String(values['current_password'] || ''), new_email: newEmail }),
          (response: ChangeEmailResponse) => `Correo actualizado a ${response.email}.`,
          (error: any) => friendlyError(error, 'Error al cambiar el correo'),
        );
        if (emailResult === null) { this.isUpdatingProfile.set(false); return; }
        const freshBusiness = this.currentBusiness();
        if (freshBusiness?.session_user) {
          this.authService.updateCurrentUser({ ...freshBusiness, session_user: { ...freshBusiness.session_user, email: emailResult.email } });
        }
      } catch (error) {
        console.error('Error al cambiar correo:', error);
        this.isUpdatingProfile.set(false);
        return;
      }
    }

    if (!otherFieldsChanged) {
      this.showEditProfile.set(false);
      this.initProfileForm();
      this.isUpdatingProfile.set(false);
      return;
    }

    try {
      const response = await this.dialogs.run(
        'Guardando perfil…',
        () => this.businessConfigService.actualizarPerfil(businessId, updateData),
        'Perfil actualizado exitosamente',
        (error: any) => friendlyError(error, 'Error al actualizar el perfil'),
      );
      if (response !== null) {
        this.showEditProfile.set(false);
        // Actualizar el usuario en el servicio de auth
        this.authService.updateCurrentUser(response.business);
        this.initProfileForm();
        window.dispatchEvent(new CustomEvent('tusala:business-configuration-changed'));
      }
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
    } finally {
      this.isUpdatingProfile.set(false);
    }
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Validar tipo de archivo
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        this.notifications.error('Formato de archivo no válido. Use PNG, JPG, JPEG, WEBP o GIF');
        return;
      }

      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.notifications.error('El archivo es demasiado grande. Máximo 5MB');
        return;
      }

      this.selectedLogo.set(file);
      
      // Crear preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.logoPreview.set(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  }

  cancelarLogo(): void {
    this.selectedLogo.set(null);
    this.logoPreview.set(null);
    const input = document.getElementById('logoInput') as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  async subirLogo(): Promise<void> {
    if (!this.cooldown.consume('config-upload-logo')) return;
    const businessId = this.currentBusiness()?.id;
    const logoFile = this.selectedLogo();

    if (!businessId || !logoFile) {
      return;
    }

    this.isUploadingLogo.set(true);
    try {
      const response = await this.dialogs.run(
        'Subiendo logo…',
        () => this.businessConfigService.actualizarLogo(businessId, logoFile),
        'Logo actualizado exitosamente',
        (error: any) => friendlyError(error, 'Error al subir el logo'),
      );
      if (response !== null) {
        // Actualizar el usuario en el servicio de auth
        this.authService.updateCurrentUser(response.business);
        this.logoLoadFailed.set(false);
        this.cancelarLogo();
        window.dispatchEvent(new CustomEvent('tusala:business-configuration-changed'));
      }
    } catch (error) {
      console.error('Error al subir logo:', error);
    } finally {
      this.isUploadingLogo.set(false);
    }
  }

  cancelarCambioPassword(): void {
    this.showChangePassword.set(false);
    this.passwordForm.reset();
    this.resetPasswordVisibility();
  }

  private resetPasswordVisibility(): void {
    this.showCurrentPassword.set(false);
    this.showNewPassword.set(false);
    this.showConfirmPassword.set(false);
  }

  abrirCambioPassword(): void {
    this.resetPasswordVisibility();
    this.initPasswordForm();
    this.showChangePassword.set(true);
  }

  async cambiarContrasena(): Promise<void> {
    if (!this.cooldown.consume('config-change-password')) return;
    const businessId = this.currentBusiness()?.id;
    if (!businessId) return;
    if (!validateAndFocusFirstError(this.passwordForm, this.passwordFormRef?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de continuar.')) return;

    const passwordData: ChangePasswordRequest = {
      current_password: this.passwordForm.get('current_password')?.value,
      new_password: this.passwordForm.get('new_password')?.value
    };

    this.isChangingPassword.set(true);
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: 'Confirmar cambio de contraseña', message: '¿Deseas actualizar la contraseña de tu cuenta?', tone: 'warning', confirmText: 'Cambiar contraseña' },
        () => this.businessConfigService.cambiarContrasena(businessId, passwordData),
        'Contraseña actualizada exitosamente',
        (error: any) => friendlyError(error, 'Error al cambiar la contraseña'),
      );
      if (result !== null) {
        this.cancelarCambioPassword();
        // El servidor cierra TODAS las sesiones al cambiar la contraseña (también
        // esta). Antes la pantalla seguía como si nada y a la siguiente acción
        // aparecía "Tu sesión expiró" sin explicación: ahora se cierra la sesión
        // aquí mismo y se pide entrar con la contraseña nueva.
        this.authService.logout();
        this.notifications.show('Tu contraseña se actualizó. Inicia sesión con tu nueva contraseña.', 'info', 7000);
        void this.router.navigate(['/login']);
      }
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
    } finally {
      this.isChangingPassword.set(false);
    }
  }

  getLogoUrl(logo: string | null | undefined): string {
    if (!logo) return '';
    return `${this.LOGOS_BASE_URL}${logo}`;
  }

  // Métodos para Políticas de Privacidad
  cargarPoliticas(): void {
    const businessId = this.currentBusiness()?.id;
    if (!businessId) {
      return;
    }

    this.isLoadingPolicies.set(true);
    this.privacyPolicyService.getPoliciesByBusiness(businessId, true).subscribe({
      next: (response) => {
        this.privacyPolicyService.policies.set(response.privacyPolicies);
        this.isLoadingPolicies.set(false);
      },
      error: (error) => {
        console.error('Error al cargar políticas:', error);
        this.isLoadingPolicies.set(false);
      }
    });
  }

  cancelarFormularioPolitica(): void {
    this.showCreatePolicyForm.set(false);
    this.editingPolicy.set(null);
    this.showPolicyTemplatePicker.set(false);
    this.initPolicyForm();
  }

  async solicitarCerrarFormularioPolitica(): Promise<void> {
    if (this.isSavingPolicy()) return;
    if (this.policyForm?.dirty) {
      const confirmed = await this.dialogs.confirm({
        title: '¿Salir sin guardar?',
        message: 'Los cambios realizados en esta política se perderán. ¿Deseas cerrar el formulario?',
        tone: 'warning',
        confirmText: 'Salir sin guardar',
        cancelText: 'Continuar editando',
      });
      if (!confirmed) return;
    }
    this.cancelarFormularioPolitica();
  }

  abrirNuevaPolitica(): void {
    this.initPolicyForm();
    this.editingPolicy.set(null);
    this.showPolicyTemplatePicker.set(false);
    this.showCreatePolicyForm.set(true);
  }

  /** Largo actual de un campo del formulario de políticas (contadores propios del modal). */
  policyLength(control: 'title' | 'content'): number {
    return String(this.policyForm?.get(control)?.value || '').length;
  }

  /** Carga la estructura elegida; si ya hay contenido, pide confirmación antes de reemplazarlo. */
  async aplicarPlantillaPolitica(templateId: 'privacy' | 'reservations' | 'cancellations' | 'complete'): Promise<void> {
    const control = this.policyForm.get('content');
    if (!control) return;
    if (String(control.value || '').trim()) {
      const confirmed = await this.dialogs.confirm({
        title: 'Reemplazar contenido',
        message: 'La plantilla elegida reemplazará el contenido que ya escribiste. ¿Deseas continuar?',
        tone: 'warning',
        confirmText: 'Usar plantilla',
        cancelText: 'Cancelar',
      });
      if (!confirmed) return;
    }
    const negocio = String(this.currentBusiness()?.name || 'nuestro negocio').trim();
    const privacy = [
      `<h2>1. Responsable</h2>`,
      `<p>${negocio} es responsable del tratamiento de los datos personales que nos compartes al reservar uno de nuestros espacios.</p>`,
      `<h2>2. Información que recopilamos</h2>`,
      `<ul>\n  <li>Nombre y datos de contacto (correo y teléfono).</li>\n  <li>Detalles de la reserva: espacio, fecha, horario y número de personas.</li>\n</ul>`,
      `<h2>3. Uso de la información</h2>`,
      `<p>Usamos tus datos únicamente para gestionar tu reserva, comunicarnos contigo sobre ella y mejorar nuestro servicio.</p>`,
      `<h2>4. Cancelaciones y reembolsos</h2>`,
      `<p>Describe aquí con cuánta anticipación se puede cancelar y en qué casos aplica un reembolso.</p>`,
      `<h2>5. Tus derechos</h2>`,
      `<p>Puedes solicitar en cualquier momento el acceso, la corrección o la eliminación de tus datos escribiéndonos a nuestro correo de contacto.</p>`,
    ];
    const reservations = [
      `<h2>1. Alcance de estas reglas</h2><p>Estas condiciones aplican a todas las reservas realizadas en ${negocio}.</p>`,
      `<h2>2. Horarios y acceso</h2><p>El ingreso y la salida deben realizarse dentro del horario confirmado en la reserva.</p>`,
      `<h2>3. Capacidad del espacio</h2><p>No se debe superar la cantidad máxima de personas indicada para el espacio reservado.</p>`,
      `<h2>4. Uso responsable</h2><p>La persona que reserva es responsable de cuidar las instalaciones, el mobiliario y los equipos disponibles.</p>`,
      `<h2>5. Convivencia</h2><p>Se debe mantener un ambiente respetuoso y evitar actividades que afecten a otros usuarios.</p>`,
      `<h2>6. Daños y objetos personales</h2><p>Describe aquí cómo se gestionan los daños y la responsabilidad sobre objetos personales.</p>`,
    ];
    const cancellations = [
      `<h2>1. Solicitud de cancelación</h2><p>Indica con cuánta anticipación debe solicitarse una cancelación.</p>`,
      `<h2>2. Cambios de fecha u horario</h2><p>Explica cuándo puede reprogramarse una reserva y si está sujeto a disponibilidad.</p>`,
      `<h2>3. Reembolsos</h2><p>Detalla en qué casos corresponde un reembolso total, parcial o no corresponde devolución.</p>`,
      `<h2>4. Inasistencia o llegada tardía</h2><p>Indica qué sucede cuando el cliente no se presenta o llega después del horario reservado.</p>`,
      `<h2>5. Cancelación por parte del negocio</h2><p>Explica las alternativas ofrecidas si ${negocio} debe cancelar una reserva confirmada.</p>`,
    ];
    const complete = [
      ...privacy.slice(0, 3),
      `<h2>4. Confirmación de la reserva</h2><p>La reserva queda confirmada cuando se completa el proceso indicado y se recibe la confirmación correspondiente.</p>`,
      ...reservations.slice(1, 4).map((section, index) => section.replace(/<h2>\d+\./, `<h2>${index + 5}.`)),
      `<h2>8. Cancelaciones, cambios y reembolsos</h2><p>Detalla aquí las condiciones aplicables para cancelar, reprogramar o solicitar una devolución.</p>`,
      `<h2>9. Contacto</h2><p>Incluye el medio por el cual tus clientes pueden realizar consultas o ejercer sus derechos.</p>`,
    ];
    const contentByTemplate = { privacy, reservations, cancellations, complete };
    const titleByTemplate = {
      privacy: 'Política de privacidad y datos',
      reservations: 'Política de reservas y uso del espacio',
      cancellations: 'Política de cancelaciones y reembolsos',
      complete: 'Políticas generales del negocio',
    };
    control.setValue(contentByTemplate[templateId].join('\n'));
    control.markAsDirty();
    control.markAsTouched();
    if (!String(this.policyForm.get('title')?.value || '').trim()) this.policyForm.get('title')?.setValue(titleByTemplate[templateId]);
    this.showPolicyTemplatePicker.set(false);
  }

  async guardarPolitica(): Promise<void> {
    if (!this.cooldown.consume('config-save-policy')) return;
    const businessId = this.currentBusiness()?.id;
    if (!businessId) return;
    if (!validateAndFocusFirstError(this.policyForm, this.policyFormRef?.nativeElement, msg => this.notifications.error(msg), 'Completa correctamente los campos marcados antes de guardar.')) return;

    const policyData = this.policyForm.value;
    const editing = this.editingPolicy();

    if (editing) {
      // Actualizar política existente
      const updateRequest: UpdatePrivacyPolicyRequest = {
        title: String(policyData.title || '').trim(),
        content: String(policyData.content || '').trim(),
        is_active: policyData.is_active
      };
      const normalize = (value: unknown) => String(value || '').trim();
      if (normalize(updateRequest.title) === normalize(editing.title)
        && normalize(updateRequest.content) === normalize(editing.content)
        && !!updateRequest.is_active === !!editing.is_active) {
        this.notifications.error('No hay cambios por guardar en la política.');
        return;
      }
      this.isSavingPolicy.set(true);
      try {
        const result = await this.dialogs.confirmAndRun(
          { title: 'Confirmar actualización', message: '¿Guardas los cambios de esta política?', tone: 'info', confirmText: 'Guardar cambios' },
          () => this.privacyPolicyService.updatePolicy(editing.id, updateRequest),
          'Política actualizada exitosamente',
          (error: any) => friendlyError(error, 'Error al actualizar la política'),
        );
        if (result !== null) {
          this.cancelarFormularioPolitica();
          this.cargarPoliticas();
          window.dispatchEvent(new CustomEvent('tusala:business-configuration-changed'));
        }
      } catch (error) {
        console.error('Error al actualizar política:', error);
      } finally {
        this.isSavingPolicy.set(false);
      }
    } else {
      // Crear nueva política
      const createRequest: CreatePrivacyPolicyRequest = {
        title: String(policyData.title || '').trim(),
        content: String(policyData.content || '').trim(),
        business_id: businessId,
        is_active: policyData.is_active
      };

      this.isSavingPolicy.set(true);
      try {
        const result = await this.dialogs.confirmAndRun(
          { title: 'Confirmar creación', message: policyData.is_active ? '¿Deseas crear y publicar esta política junto con las demás políticas activas?' : '¿Deseas crear esta política como borrador?', tone: policyData.is_active ? 'info' : 'info', confirmText: 'Crear política' },
          () => this.privacyPolicyService.createPolicy(createRequest),
          'Política creada exitosamente',
          (error: any) => friendlyError(error, 'Error al crear la política'),
        );
        if (result !== null) {
          this.cancelarFormularioPolitica();
          this.cargarPoliticas();
          window.dispatchEvent(new CustomEvent('tusala:business-configuration-changed'));
        }
      } catch (error) {
        console.error('Error al crear política:', error);
      } finally {
        this.isSavingPolicy.set(false);
      }
    }
  }

  editarPolitica(policy: PrivacyPolicy): void {
    this.showCreatePolicyForm.set(false);
    this.editingPolicy.set(policy);
    this.policyForm.patchValue({
      title: policy.title,
      content: policy.content,
      is_active: policy.is_active
    });
  }

  verPolitica(policy: PrivacyPolicy): void {
    this.viewingPolicy.set(policy);
  }

  cerrarModalPolitica(): void {
    this.viewingPolicy.set(null);
  }

  async activarPolitica(id: number): Promise<void> {
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: 'Publicar política', message: 'Esta política se mostrará junto con las demás políticas publicadas del negocio.', tone: 'info', confirmText: 'Publicar' },
        () => this.privacyPolicyService.activatePolicy(id),
        'Política activada exitosamente',
        (error: any) => friendlyError(error, 'Error al activar la política'),
      );
      if (result !== null) {
        this.cargarPoliticas();
        window.dispatchEvent(new CustomEvent('tusala:business-configuration-changed'));
      }
    } catch (error) {
      console.error('Error al activar política:', error);
    }
  }

  async desactivarPolitica(id: number): Promise<void> {
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: 'Desactivar política', message: 'La política dejará de mostrarse en el proceso de reserva.', tone: 'danger', confirmText: 'Desactivar' },
        () => this.privacyPolicyService.deactivatePolicy(id),
        'Política desactivada exitosamente',
        (error: any) => friendlyError(error, 'Error al desactivar la política'),
      );
      if (result !== null) this.cargarPoliticas();
    } catch (error) {
      console.error('Error al desactivar política:', error);
    }
  }

  async eliminarPolitica(id: number): Promise<void> {
    try {
      const result = await this.dialogs.confirmAndRun(
        { title: 'Eliminar política', message: 'Esta acción no se puede deshacer.', tone: 'danger', confirmText: 'Eliminar' },
        () => this.privacyPolicyService.deletePolicy(id),
        'Política eliminada exitosamente',
        (error: any) => friendlyError(error, 'Error al eliminar la política'),
      );
      if (result !== null) this.cargarPoliticas();
    } catch (error) {
      console.error('Error al eliminar política:', error);
    }
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}
